import { supabase } from '../lib/supabase';
import { buscarComissaoPessoa, buscarGestorComercial, inserirComissao, calcularMesPagamento } from './comissoes';
import { logError } from '../lib/logError';
import { normalizePhone } from '../lib/phone';
import { adaptReuniaoParaAgendamento, adaptVenda } from './ghlReadAdapter';
import { toNewConfig, pontoDaAcao, cargoPontuaAcao, configVigenteEm, arredondaPontos, motivoBloqueioTrocaAcao } from './pontosConfig';
import { calculateFirstMonthCash, commissionCargoKey, commissionRuleCargoKey, getCommissionRulesConfig, resolveCommissionManagerRecipient, resolveCommissionPolicy, resolvePreSaleParticipation } from './commissionRules';
import { buscarSocialSellerDoLead, resolveSocialSellerParticipation } from './socialSeller';
import { hojeSP, diaSP, horaSP, ancoraMeioDiaSP, somarMeses } from '../lib/tz';
import { resolverProdutoOuFallback, avisarSeAdivinhou } from './produtoResolver';
import { criarCacheComTtl } from '../lib/cacheComTtl';

// ── Dual-write Fase 2: resolução nome→id para popular *_id nas escritas ───────
// Cacheia bd_pessoas (nome canônico → id). Nunca lança — retorna null se não achar
// (a coluna de texto continua sendo escrita; id apenas enriquece o vínculo).
let _pessoaIdCache = null;
export const invalidatePessoaIdCache = () => { _pessoaIdCache = null; };
export const resolvePessoaId = async (nome) => {
    if (!nome) return null;
    if (!_pessoaIdCache) {
        const { data, error } = await supabase.from('bd_pessoas').select('id, nome');
        if (error) { await logError(error, { fn: 'resolvePessoaId' }); return null; }
        _pessoaIdCache = new Map((data || []).map(p => [String(p.nome || '').trim().toLowerCase(), p.id]));
    }
    return _pessoaIdCache.get(String(nome).trim().toLowerCase()) || null;
};

// ── Ledger de Pontos — helpers internos ──────────────────────────────────────

const DEFAULT_PRODUTOS = [
    { slug: 'performance', label: 'Performance', keywords: ['performance'] },
    { slug: 'expansao',    label: 'Expansão',    keywords: ['expans'] },
    { slug: 'tracao',      label: 'Tração',      keywords: ['tra'] },
    { slug: 'pcl',         label: 'PCL',         keywords: [] },
];

// Com prazo: sem ele, uma aba aberta antes de uma edição de catálogo nunca via o produto novo,
// e resolvia a venda pelo catálogo velho. Ver `lib/cacheComTtl.js`.
const _produtosCache = criarCacheComTtl(async () => {
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'produtos_config').single();
    return Array.isArray(data?.value) && data.value.length > 0 ? data.value : DEFAULT_PRODUTOS;
});
export async function getProducts() { return _produtosCache.ler(); }
export function invalidateProdutosCache() { _produtosCache.invalidar(); }

// SYNC: schema/valores em src/services/pontosConfig.js (DEFAULT_CONFIG) e no dashboard.
// Fonte de verdade em runtime: app_settings.gamification_weights_rankings
const _weightsCache = criarCacheComTtl(async () => {
    const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'gamification_weights_rankings')
        .single();
    return toNewConfig(data?.value); // aceita formato antigo e novo
});
async function getWeights() { return _weightsCache.ler(); }
export function invalidateWeightsCache() { _weightsCache.invalidar(); _versoesCache = null; _feriadosCache = null; }

// ── Pontuação com vigência ───────────────────────────────────────────────────
// O peso de um ponto é o que valia NA DATA DO PONTO, não o que vale hoje. Sem isso, mexer no
// peso para uma campanha muda pontuação já publicada (foi o que aconteceu em 2026-08: o sweep
// do dashboard recalculou julho inteiro com a escala nova).
let _versoesCache = null;
async function getConfigVersoes() {
    if (_versoesCache) return _versoesCache;
    const { data, error } = await supabase
        .from('bd_pontos_config_versoes')
        .select('vigencia_inicio, value');
    if (error) {
        await logError(error, { fn: 'getConfigVersoes' });
        return []; // sem versões → o chamador cai na config atual (ver getWeightsParaData)
    }
    _versoesCache = data ?? [];
    return _versoesCache;
}

let _feriadosCache = null;
async function getFeriados() {
    if (_feriadosCache) return _feriadosCache;
    const { data, error } = await supabase.from('bd_feriados').select('data').eq('ativo', true);
    if (error) {
        await logError(error, { fn: 'getFeriados' });
        return new Set();
    }
    _feriadosCache = new Set((data ?? []).map((f) => String(f.data).slice(0, 10)));
    return _feriadosCache;
}

/**
 * Config + feriados para calcular um ponto numa data.
 *
 * Sem versão cobrindo a data, cai na config atual e registra o aviso — de propósito, e ao
 * contrário do sweep do dashboard, que nesse caso para. A assimetria é deliberada: aqui um
 * humano está registrando um agendamento e travar o formulário atrapalharia a operação por um
 * problema de configuração; lá é um cron reescrevendo histórico em massa, onde parar é o certo.
 */
/**
 * Reexportado de `lib/tz.js`, que é a fonte única do fuso do negócio. Continua saindo daqui
 * porque `Comissionamento.jsx` e `MetasPontuacao.jsx` já importam `hojeSP` daqui, e porque este
 * arquivo o usa em 11 lugares internos. A implementação, essa, é uma só.
 */
export { hojeSP, diaSP, horaSP };

/**
 * Grava uma nova vigência da config de pontuação. `vigencia_inicio` é a data A PARTIR da qual
 * os pesos valem — o passado fica como está. Repetir a mesma data sobrescreve aquela vigência
 * (a analista pode corrigir um valor no mesmo dia sem criar uma pilha de versões).
 */
export async function salvarVersaoPontuacao(value, vigenciaInicio, nota) {
    const { error } = await supabase
        .from('bd_pontos_config_versoes')
        .upsert([{ vigencia_inicio: vigenciaInicio, value, nota: nota ?? null }], { onConflict: 'vigencia_inicio' });
    if (error) {
        await logError(error, { fn: 'salvarVersaoPontuacao', vigenciaInicio });
        throw new Error(`[bd_pontos_config_versoes] ${error.message}`);
    }
    _versoesCache = null;
}

export async function listarVersoesPontuacao() {
    const { data, error } = await supabase
        .from('bd_pontos_config_versoes')
        .select('vigencia_inicio, value, nota, created_at')
        .order('vigencia_inicio', { ascending: false });
    if (error) { await logError(error, { fn: 'listarVersoesPontuacao' }); return []; }
    return data ?? [];
}

export async function listarFeriados() {
    const { data, error } = await supabase
        .from('bd_feriados').select('data, nome, escopo, ativo').order('data');
    if (error) { await logError(error, { fn: 'listarFeriados' }); return []; }
    return data ?? [];
}

export async function salvarFeriado({ data, nome, escopo = 'nacional', ativo = true }) {
    const { error } = await supabase
        .from('bd_feriados').upsert([{ data, nome, escopo, ativo }], { onConflict: 'data' });
    if (error) { await logError(error, { fn: 'salvarFeriado', data }); throw new Error(`[bd_feriados] ${error.message}`); }
    _feriadosCache = null;
}

export async function removerFeriado(data) {
    const { error } = await supabase.from('bd_feriados').delete().eq('data', data);
    if (error) { await logError(error, { fn: 'removerFeriado', data }); throw new Error(`[bd_feriados] ${error.message}`); }
    _feriadosCache = null;
}

export async function getWeightsParaData(dataISO) {
    const [versoes, feriados] = await Promise.all([getConfigVersoes(), getFeriados()]);
    const versao = configVigenteEm(versoes, dataISO);
    if (versao) return { config: toNewConfig(versao.value), feriados };
    if (versoes.length) {
        await logError(new Error(`sem config vigente para ${dataISO} — usando a config atual`), { fn: 'getWeightsParaData', data: dataISO });
    }
    return { config: await getWeights(), feriados };
}

/**
 * Caminho de ESCRITA: rótulo do GHL → slug de produto.
 *
 * Deploy A (modo sombra): a regra saiu daqui e virou `produtoResolver.js`, mas o comportamento
 * observável é o MESMO de antes — inclusive o fallback para o último da lista e o `?? 'pcl'`.
 * A única diferença é o `console.warn('[produto] ...')` quando o slug foi ADIVINHADO, para
 * medir em produção quantos rótulos por dia dependem disso antes de passar a recusar.
 * Não remover o `?? 'pcl'` sem Deploy B: hoje ele é o que impede um slug `null` de chegar ao
 * `w.closer[produto]` e gravar 0 calado.
 */
function normalizeProduct(produto, produtos) {
    const list = Array.isArray(produtos) && produtos.length > 0 ? produtos : DEFAULT_PRODUTOS;
    const r = resolverProdutoOuFallback(produto, list);
    const slug = r.slug ?? 'pcl';
    avisarSeAdivinhou('api.normalizeProduct', produto, r, slug);
    return slug;
}

function classifyPaymentLocal(valorEntrada, valorTotal, formaParcelas, numParcelas) {
    if (!numParcelas || numParcelas <= 1) return 'a_vista';
    const pct = valorTotal > 0 ? (valorEntrada / valorTotal) * 100 : 0;
    if (pct >= 31) return 'caixa_31_50';
    if (pct >= 20) return 'caixa_20_30';
    const forma = (formaParcelas || '').toLowerCase();
    if (forma.includes('cart') || forma.includes('crédit') || forma.includes('credit')) return 'parcelado_cartao';
    if (forma.includes('boleto')) return 'parcelado_boleto';
    return 'parcelado_pix';
}

/**
 * ⛔ PONTO QUE VALE 0 NÃO VIRA REGISTRO (2026-08-28, decisão do dono).
 *
 * A pontuação continua LIGADA na config — está pausada, não desligada. Enquanto valer zero ela
 * simplesmente não é escrita; no dia em que ganhar peso volta a gerar sozinha, sem mexer em mais
 * nada. Aqui isso significa: o objeto que viajaria em `pontos_pendentes` vira `null`, e a
 * comissão não carrega ponto nenhum.
 *
 * ⚠️ A INTERAÇÃO COM `inserirComissao` (`comissoes.js`) É O QUE NÃO PODE QUEBRAR. Lá o ponto não
 * tem linha própria: ele viaja dentro da comissão, e a RPC `approve_venda` (repo
 * `painel-de-comissoes`) só o materializa lendo `WHERE pontos_pendentes IS NOT NULL`, sem olhar
 * `ganho`. Por isso `inserirComissao` grava de propósito a comissão de ganho 0 que carrega ponto.
 * Com este `null`, a matriz de casos fica:
 *   - ganho 0 e ponto 0   → nada é inserido (não há dinheiro nem ponto a carregar);
 *   - ganho 0 e ponto > 0 → insere a comissão zerada, que é o VEÍCULO do ponto (preservado);
 *   - ganho > 0 e ponto 0 → insere a comissão, sem ponto.
 *
 * Por que zerar aqui e não depois da blindagem de `comissoes.js`: `blindarPontosPendentes`
 * recalcula o mesmo número. Ela resolve o slug com `resolverSlugProduto`, que tem paridade
 * provada em teste com o `normalizeProduct` usado acima, e lê a mesma matriz — então onde este
 * cálculo dá 0, o dela também dá 0 (seja `zero-declarado`, seja `sem-matriz`, que vira 0 com
 * `[zero-nao-calculado]`). Zerar antes não descarta nenhum valor que a blindagem resgataria; só
 * torna a marca de zero desnecessária, porque a linha de zero deixa de nascer.
 *
 * `=== 0` e não `<= 0`: a regra do dono é sobre pontuação zerada. Peso negativo, se um dia
 * existir, é uma penalidade escolhida e continua sendo gravada.
 */
/**
 * Caixa do primeiro mês: o que a EMPRESA recebe no mês da venda.
 *
 * Antes lia só `entradasParsed`, ou seja, o bloco "Entradas" do formulário. Uma venda de
 * R$ 54.000 registrada como 100% no cartão, sem entrada, apurava caixa ZERO e não pagava
 * bônus nenhum — enquanto `classifyPaymentLocal` classificava a mesma venda como `a_vista`.
 * O mesmo submit se contradizia.
 *
 * `linhas` já modela o recebimento corretamente e é a fonte certa: entrada vira uma linha por
 * pagamento; boleto, uma linha por parcela, mês a mês; e cartão, UMA linha com o valor cheio,
 * porque o dinheiro do cartão cai de uma vez. Venda 100% no cartão é, por isso, 100% de cash
 * collect — é o que o negócio chama de à vista.
 */
function caixaDoPrimeiroMes(linhas) {
    return calculateFirstMonthCash((linhas ?? []).map((l) => ({ valor: l.valor, data: l.data_venda })));
}

function semPontoQuandoZerado(ponto) {
    return ponto && Number(ponto.pontos) === 0 ? null : ponto;
}

export const getAgendadores = async () => {
    const { data, error } = await supabase
        .from('bd_pessoas')
        .select('*')
        .neq('atividade', 'inativo')
        .neq('nome', 'Sem SDR')
        .order('nome');
    if (error) throw error;
    return data;
};

export const getClosers = async () => {
    const data = await getAgendadores();
    return data.filter(p => {
        const cargo = p.cargo?.trim().toLowerCase();
        return cargo === 'closer' || cargo === 'sales farmer';
    });
};

export const getCurrentCloser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) return null;
    const { data, error } = await supabase
        .from('bd_pessoas')
        .select('*')
        .eq('email', user.email)
        .in('cargo', ['closer', 'Closer', 'sales farmer', 'Sales Farmer'])
        .neq('atividade', 'inativo')
        .limit(1);
    if (error || !data?.length) return null;
    return data[0];
};

export const getTodasPessoas = async () => {
    const { data, error } = await supabase
        .from('bd_pessoas')
        .select('*')
        .order('nome');
    if (error) throw error;
    return data;
};

export const getSDRs = async () => {
    const { data, error } = await supabase
        .from('bd_pessoas')
        .select('nome, status_dia, ghl_user_id')
        .eq('cargo', 'SDR')
        .neq('atividade', 'inativo')
        .neq('nome', 'Sem SDR')
        .order('nome');
    if (error) {
        await logError(error, { fn: 'getSDRs' });
        throw error;
    }
    return data;
};

export const updateStatusDia = async (nome, status) => {
    const novoStatus = status ? 1 : 0;
    const { error } = await supabase
        .from('bd_pessoas')
        .update({ status_dia: novoStatus })
        .eq('nome', nome);
    if (error) {
        await logError(error, { fn: 'updateStatusDia', nome, status: novoStatus });
        throw error;
    }
    return { success: true };
};

export const submitAgendamento = async (formData) => {
    // Telefone no canônico do ecossistema (55 + DDD + número) antes de gravar/buscar.
    formData = { ...formData, telefone: normalizePhone(formData.telefone) };
    const { data, error } = await supabase.rpc('submit_agendamento', { payload: formData });
    if (error) {
        await logError(error, { fn: 'submitAgendamento', telefone: formData.telefone });
        if (error.message?.includes('month_lock_closed')) {
            throw new Error('Mês de pagamento fechado pelo financeiro. Contate o responsável antes de lançar esta comissão.');
        }
        throw error;
    }

    // Busca o agendamento recém-criado para gravar horário e pontos
    const { data: ag, error: errAg } = await supabase
        .from('bd_agendamentos')
        .select('id')
        .eq('telefone', formData.telefone)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
    if (errAg) {
        await logError(errAg, { fn: 'submitAgendamento/lookup', telefone: formData.telefone });
    } else if (ag?.id) {
        // Persiste campos não mapeados (ou potencialmente incorretos) pela RPC
        const extraFields = {};
        if (formData.horarioReuniao) extraFields.horario_reuniao = formData.horarioReuniao;
        if (formData.closerAssigned) extraFields.closer_assigned = formData.closerAssigned;
        // Garante data_agendamento correta no banco (RPC pode gravar NOW() em vez do payload)
        if (formData.dataAgendamento) extraFields.data_agendamento = formData.dataAgendamento;
        // Dual-write Fase 2: vincula agendador/closer por id (a RPC só grava o texto)
        const agendadorId = await resolvePessoaId(formData.agendador);
        if (agendadorId) extraFields.agendador_id = agendadorId;
        if (formData.closerAssigned) {
            const closerId = await resolvePessoaId(formData.closerAssigned);
            if (closerId) extraFields.closer_id = closerId;
        }
        // Rodada definida NO AGENDAMENTO: R1 por padrão; Continuação envia a rodada escolhida.
        // A realização passa a ser marcada via tipo_reuniao na conclusão (não mais pelo numero).
        extraFields.numero_reuniao = formData.numeroReuniao || 'R1';
        if (Object.keys(extraFields).length > 0) {
            const { error: errUpd } = await supabase
                .from('bd_agendamentos')
                .update(extraFields)
                .eq('id', ag.id);
            if (errUpd) await logError(errUpd, { fn: 'submitAgendamento/extras/update', id: ag.id });
        }

        // Gravar pontos no ledger — config-driven (cargo × ação × valor)
        if (formData.agendador) {
            const dateStr = formData.dataAgendamento || hojeSP();
            const { config: w, feriados } = await getWeightsParaData(dateStr);
            const cargo = formData.cargo || 'SDR';
            const TIPO_ACAO_MAP = {
                'Novo Agendamento': 'agendaCriada',
                'Reagendamento': 'reagendamento',
                'Agendamento de Transferido': 'agendamentoTransferido',
            };
            const acaoKey = TIPO_ACAO_MAP[formData.tipoAgendamento];
            const pontoRows = [];
            if (acaoKey && cargoPontuaAcao(cargo, acaoKey, w)) {
                pontoRows.push({
                    pessoa: formData.agendador, pessoa_id: agendadorId, cargo,
                    acao: acaoKey, pontos: pontoDaAcao(cargo, acaoKey, w, { data: dateStr, feriados }),
                    referencia_id: ag.id, referencia_tabela: 'bd_agendamentos', data: dateStr,
                });
                if (formData.dataAgendamento && formData.dataReuniao && formData.dataAgendamento === formData.dataReuniao
                    && cargoPontuaAcao(cargo, 'reuniaoInstantanea', w)) {
                    pontoRows.push({
                        pessoa: formData.agendador, pessoa_id: agendadorId, cargo,
                        acao: 'reuniaoInstantanea', pontos: pontoDaAcao(cargo, 'reuniaoInstantanea', w, { data: dateStr, feriados }),
                        referencia_id: ag.id, referencia_tabela: 'bd_agendamentos', data: dateStr,
                    });
                }
            }
            if (pontoRows.length) {
                const { error: errPontos } = await supabase
                    .from('bd_pontos')
                    .upsert(pontoRows, { onConflict: 'referencia_id,acao', ignoreDuplicates: false });
                if (errPontos) throw new Error(`[bd_pontos] ${errPontos.message}`);
            }
        }
    }

    // Registra comissão do agendador DIRETAMENTE em bd_comissoes (status=approved).
    // Agendamentos não passam pelo fluxo de aprovação do painel — volume alto e automático.
    // Silencioso se ganho <= 0 (cargo sem taxa configurada).
    if (formData.agendador) {
        try {
            const dadosAgendador = await buscarComissaoPessoa(formData.agendador);
            const ganhoAg = dadosAgendador.comissao_agendamento;
            if (ganhoAg && ganhoAg > 0) {
                const mesPagamento = calcularMesPagamento(formData.dataAgendamento);
                const { error: errCom } = await supabase.from('bd_comissoes').insert({
                    pessoa: formData.agendador,
                    pessoa_id: dadosAgendador.id,
                    tipo: 'Agendamento',
                    cargo: dadosAgendador.cargo,
                    cargo_id: dadosAgendador.cargo_id,
                    cliente: formData.nome,
                    ganho: ganhoAg,
                    data_base: formData.dataAgendamento || null,
                    mes_pagamento: mesPagamento,
                    telefone: formData.telefone || '',
                    status: 'approved',
                    approved_by: 'desk-auto',
                    approved_at: new Date().toISOString(),
                    created_by: 'desk',
                });
                if (errCom) {
                    await logError(errCom, { fn: 'submitAgendamento/comissao-insert', agendador: formData.agendador });
                }
            }
        } catch (errComissao) {
            // Não quebra o fluxo principal — agendamento já foi gravado
            await logError(errComissao, { fn: 'submitAgendamento/comissao', agendador: formData.agendador });
        }
    }

    return data;
};

/**
 * Resolve quem recebe o override de gestão nesta venda.
 *
 * A escolha explícita do formulário vence. Quando não informada, cai no gestor comercial ativo
 * (cargo com `comissao_venda_override_valor`). Antes, deixar o campo desmarcado simplesmente não
 * lançava override — 9 vendas do time ficaram sem, e o gestor deixou de receber sobre elas.
 *
 * Devolve `{ nome: null }` quando não há gestor configurado: aí nenhum override é lançado.
 */
const resolverGestorDaVenda = async ({ temGestorComercial, gestorComercial }) => {
    const SEM_GESTOR = { nome: null, pessoa: { comissao_venda: 0, comissao_venda_override: 0, cargo: 'Gestor Comercial', cargo_id: null, id: null } };
    if (temGestorComercial && gestorComercial) {
        return { nome: gestorComercial, pessoa: await buscarComissaoPessoa(gestorComercial) };
    }
    const auto = await buscarGestorComercial();
    if (!auto?.nome) return SEM_GESTOR;
    return { nome: auto.nome, pessoa: await buscarComissaoPessoa(auto.nome) };
};

const inserirBonusFixoDaVenda = async ({ bonuses, pessoa, pessoaId, cargo, cargoId, cliente, valorBase, dataBase, telefone, vendaId }) => {
    for (const bonus of bonuses || []) {
        await inserirComissao({
            pessoa,
            pessoaId,
            tipo: `Bônus · ${bonus.name}`,
            cargo,
            cargoId,
            cliente,
            ganho: bonus.amount,
            valorBase,
            dataBase,
            telefone,
            vendaId,
        });
    }
};

/** Formas que o gerador de linhas de recebimento sabe transformar em parcelas. */
const FORMAS_DE_RESTANTE = ['Cartão de Crédito', 'Boleto'];

/**
 * Restante informado precisa de forma E quantidade de parcelas — lança antes de qualquer escrita.
 *
 * O gerador de linhas só conhece cartão e boleto, e só roda com `qtdParcelas > 0`. O formulário
 * deixava passar "valor a parcelar" com a forma em "Selecione" ou sem quantidade, e a venda
 * nascia com `valor_total` cheio mas SEM NENHUMA linha de receita nem de comissão do restante —
 * sem erro, sem log. R$ 54.000 com R$ 5.400 de entrada pagava comissão sobre R$ 5.400.
 *
 * Aqui, e não na tela, porque os dois caminhos (`submitVenda` e `submitVendaTicket`) passam
 * por esta função, e o operador vê a mensagem dizendo o que falta.
 */
function exigirParcelamentoCompleto({ valorParcelarNum, qtdParcelas, formaPagamentoParcelas }) {
    if (!(valorParcelarNum > 0)) return;
    const faltas = [];
    if (!FORMAS_DE_RESTANTE.includes(formaPagamentoParcelas)) faltas.push('a forma de parcelamento (Cartão de Crédito ou Boleto)');
    if (!(qtdParcelas > 0)) faltas.push('a quantidade de parcelas');
    if (faltas.length === 0) return;
    const err = new Error(`Há R$ ${valorParcelarNum.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} a parcelar, mas falta ${faltas.join(' e ')}. Complete o parcelamento antes de registrar — sem isso o restante ficaria sem receita e sem comissão.`);
    err.code = 'PARCELAMENTO_INCOMPLETO';
    throw err;
}

/**
 * Torna EXPLÍCITA a linha de pré-venda que deixou de nascer.
 *
 * Ausência de linha é indistinguível de perda silenciosa quando ninguém registra o motivo — é
 * exatamente o erro que `inserirComissao` já corrigiu para `ganho 0` e `sem pessoa`. Aqui a
 * linha não nasce de propósito (ver `resolvePreSaleParticipation`), e quem for conferir a
 * folha meses depois precisa achar o porquê sem ler o código.
 *
 * Dois destinos, de propósito: `console.warn` com prefixo `[papel]` (grepável no DevTools do
 * operador, mesma convenção do `[produto]`) e `logError` com `severity: 'warning'`, que é o
 * que de fato chega a `app_error_events`/Sentry e sobrevive ao fechamento da aba.
 * Nunca lança: suprimir a linha é o comportamento correto, não um erro de execução.
 *
 * Uma chamada por VENDA, não por parcela — é decisão constante no loop de `linhas`.
 */
const avisarPreVendaSuprimida = async ({ preVenda, origem, vendedor, preVendedor, cliente, vendaId }) => {
    if (preVenda.gerar || preVenda.motivo !== 'mesma-pessoa-que-vendedor') return;
    const detalhe = `${origem}: "${preVendedor}" fez a própria pré-venda de "${cliente}" — `
        + `linha "Venda (Pré-venda)" NÃO gerada; quem vende e pré-vende recebe só o do cargo dele `
        + `(critério: ${preVenda.criterio}). Exceção entra por comissão manual.`;
    console.warn(`[papel] ${detalhe}`);
    await logError(new Error(`[papel] ${detalhe}`), {
        fn: origem,
        codigo: 'pre-venda-suprimida',
        severity: 'warning',
        vendedor,
        preVendedor,
        cliente,
        vendaId,
        criterio: preVenda.criterio,
    });
};

export const submitVenda = async (dadosVenda) => {
    const [pessoaVendedor, pessoaPreVendedor, gestorDaVenda, coletadoSS] = await Promise.all([
        buscarComissaoPessoa(dadosVenda.vendedor),
        buscarComissaoPessoa(dadosVenda.preVendedor),
        resolverGestorDaVenda(dadosVenda),
        buscarSocialSellerDoLead({ telefone: dadosVenda.telefone, leadId: dadosVenda.leadId ?? dadosVenda.lead_id ?? null }),
    ]);
    // Social Seller que coletou o número deste lead recebe linha própria (ver `socialSeller.js`).
    // Resolvido aqui, antes de qualquer escrita, pelo mesmo motivo das buscas acima.
    const socialSeller = resolveSocialSellerParticipation({
        coletado: coletadoSS,
        sellerId: pessoaVendedor.id,
        sellerName: dadosVenda.vendedor,
        preSaleId: pessoaPreVendedor.id,
        preSaleName: dadosVenda.preVendedor,
    });
    const pessoaSocialSeller = await buscarComissaoPessoa(socialSeller.gerar ? socialSeller.nome : null);
    const cargoSocialSeller = pessoaSocialSeller.cargo || 'Social Seller';
    const cargoIdSocialSeller = pessoaSocialSeller.cargo_id || null;
    const comissaoSocialSeller = pessoaSocialSeller.comissao_venda || 0;

    const comissaoVendedor = pessoaVendedor.comissao_venda || 0;
    const cargoVendedor = pessoaVendedor.cargo || 'Closer';
    const cargoIdVendedor = pessoaVendedor.cargo_id || null;
    const gestorEfetivo = resolveCommissionManagerRecipient({
        sellerName: dadosVenda.vendedor,
        seller: pessoaVendedor,
        managerResolution: gestorDaVenda,
    });
    const pessoaGestor = gestorEfetivo.pessoa;
    const nomeGestor = gestorEfetivo.nome;
    const cargoPreVendedor = pessoaPreVendedor.cargo || '';
    const cargoIdPreVendedor = pessoaPreVendedor.cargo_id || null;
    const cargoGestor = pessoaGestor.cargo || 'Gestor Comercial';
    const cargoIdGestor = pessoaGestor.cargo_id || null;
    // ⛔ A taxa vem do PAPEL na venda, não do cargo da pessoa (2026-08-31).
    // Vendedor == pré-vendedor ⇒ o papel de pré-venda não existe nesta venda e NADA nasce por
    // ele. Ver `resolvePreSaleParticipation` para o defeito medido e o critério de identidade.
    const preVenda = resolvePreSaleParticipation({
        sellerName: dadosVenda.vendedor,
        sellerId: pessoaVendedor.id,
        preSaleName: dadosVenda.preVendedor,
        preSaleId: pessoaPreVendedor.id,
    });
    // Sales Farmer que agendou para outro closer recebe comissao_indicacao (taxa Y), não comissao_venda (taxa X)
    const isSFIndicacao = cargoPreVendedor.trim().toLowerCase() === 'sales farmer' && dadosVenda.preVendedor !== dadosVenda.vendedor;
    const comissaoPreVendedor = isSFIndicacao
        ? (pessoaPreVendedor.comissao_indicacao || 0)
        : (pessoaPreVendedor.comissao_venda || 0);
    // Override de gestão: taxa sobre venda de LIDERADO, distinta da taxa de venda própria do
    // gestor. Enquanto o cargo não tiver a taxa separada, buscarComissaoPessoa devolve a de venda
    // própria — o valor pago não muda até a taxa ser configurada.
    const comissaoGestor = pessoaGestor.comissao_venda_override ?? pessoaGestor.comissao_venda ?? 0;

    const entradasParsed = (dadosVenda.entradas || [])
        .map(e => ({
            valor: parseFloat(e.valor) || 0,
            formaPagamento: e.formaPagamento || 'Nao Informado',
            data: e.data || hojeSP(),
        }))
        .filter(e => e.valor > 0);

    const valorEntradaTotal = entradasParsed.reduce((s, e) => s + e.valor, 0);
    const valorParcelar = parseFloat(dadosVenda.valorParcelar) || 0;
    const qtdParcelas = parseInt(dadosVenda.parcelas) || 0;
    exigirParcelamentoCompleto({ valorParcelarNum: valorParcelar, qtdParcelas, formaPagamentoParcelas: dadosVenda.formaPagamentoParcelas });

    const ultimaDataEntrada = entradasParsed.length > 0
        ? [...entradasParsed].map(e => e.data).sort().at(-1)
        : hojeSP();
    const baseDateString = entradasParsed[0]?.data || ultimaDataEntrada;

    const linhas = [];

    entradasParsed.forEach((e, i) => {
        linhas.push({
            parcela: `${i + 1}/${entradasParsed.length}`,
            valor: e.valor,
            forma_pagamento: e.formaPagamento,
            data_venda: e.data,
            tipo_linha: `Entrada/${e.formaPagamento}`,
        });
    });

    if (valorParcelar > 0 && qtdParcelas > 0) {
        // Simetria deliberada com `submitVendaTicket` — ver a justificativa completa lá. O offset
        // vale só para boleto, e a soma de meses grampeia o dia.
        const offsetBoleto = entradasParsed.length === 0 ? 0 : 1;
        if (dadosVenda.formaPagamentoParcelas === 'Cartão de Crédito') {
            linhas.push({
                parcela: `1/${qtdParcelas}`,
                valor: valorParcelar,
                forma_pagamento: 'Cartão de Crédito',
                data_venda: ultimaDataEntrada,
                tipo_linha: 'Restante/Cartao de Credito',
            });
        } else if (dadosVenda.formaPagamentoParcelas === 'Boleto') {
            const valorPorParcela = valorParcelar / qtdParcelas;
            for (let i = 1; i <= qtdParcelas; i++) {
                linhas.push({
                    parcela: `${i}/${qtdParcelas}`,
                    valor: valorPorParcela,
                    forma_pagamento: 'Boleto',
                    data_venda: somarMeses(ultimaDataEntrada, i - 1 + offsetBoleto),
                    tipo_linha: 'Restante/Boleto',
                });
            }
        }
    }

    const valorTotal = valorEntradaTotal + valorParcelar;
    const formasPag = [
        ...entradasParsed.map(e => e.formaPagamento),
        ...(valorParcelar > 0 && dadosVenda.formaPagamentoParcelas ? [dadosVenda.formaPagamentoParcelas] : []),
    ];

    // Faturamento primeiro para capturar o ID que vincula receitas e comissões
    const { data: vendaData, error: errFat } = await supabase.from('bd_vendas_faturamento_pendentes').insert({
        telefone: normalizePhone(dadosVenda.telefone),
        nome: dadosVenda.nome,
        email: dadosVenda.email,
        vendedor: dadosVenda.vendedor,
        vendedor_id: pessoaVendedor.id || null,
        pre_vendedor: dadosVenda.preVendedor || '',
        pre_vendedor_id: pessoaPreVendedor.id || null,
        produto: dadosVenda.produto || '',
        parcelas: dadosVenda.parcelas || '1',
        valor_total: valorTotal,
        valor_entrada: valorEntradaTotal,
        forma_pagamento: formasPag.length > 0 ? formasPag.join('/') : 'Não Informado',
        data_venda: baseDateString || null,
    }).select('id').single();
    if (errFat) throw errFat;

    for (const linha of linhas) {
        const { error } = await supabase.from('bd_vendas_receita_pendentes').insert({
            telefone: normalizePhone(dadosVenda.telefone),
            nome: dadosVenda.nome,
            email: dadosVenda.email,
            vendedor: dadosVenda.vendedor,
            vendedor_id: pessoaVendedor.id || null,
            pre_vendedor: dadosVenda.preVendedor || '',
            pre_vendedor_id: pessoaPreVendedor.id || null,
            produto: dadosVenda.produto || '',
            parcela: linha.parcela,
            valor: linha.valor,
            forma_pagamento: linha.forma_pagamento,
            data_venda: linha.data_venda,
            tipo_linha: linha.tipo_linha,
            venda_id: vendaData.id,
        });
        if (error) throw error;
    }

    // Calcular pontos antecipadamente para guardar em pontos_pendentes (criados na aprovação)
    const dateStr = baseDateString || hojeSP();
    const [{ config: w, feriados }, produtosList, commissionRules] = await Promise.all([
        getWeightsParaData(dateStr),
        getProducts(),
        getCommissionRulesConfig(),
    ]);
    const produto = normalizeProduct(dadosVenda.produto, produtosList);
    const customComissoes = produtosList.find(p => p.slug === produto)?.comissoes ?? {};
    const comissaoVendedorFinal = customComissoes[commissionCargoKey(cargoVendedor)] ?? comissaoVendedor;
    const comissaoPreVendedorFinal = customComissoes[commissionCargoKey(cargoPreVendedor)] ?? comissaoPreVendedor;
    const comissaoGestorFinal = customComissoes[commissionCargoKey(cargoGestor)] ?? comissaoGestor;
    const paymentCat = classifyPaymentLocal(
        valorEntradaTotal,
        valorTotal,
        dadosVenda.formaPagamentoParcelas,
        qtdParcelas
    );
    const policyContext = {
        productSlug: produto,
        paymentCategory: paymentCat,
        contractTotal: valorTotal,
        firstMonthCash: caixaDoPrimeiroMes(linhas),
        eventDate: dateStr,
    };
    const policyVendedor = resolveCommissionPolicy({ ...policyContext, participantContext: 'seller', cargo: cargoVendedor, baseRate: comissaoVendedorFinal }, commissionRules);
    const policyPreVendedor = resolveCommissionPolicy({ ...policyContext, participantContext: 'pre_sale', cargo: cargoPreVendedor, baseRate: comissaoPreVendedorFinal }, commissionRules);
    const policyGestor = resolveCommissionPolicy({ ...policyContext, participantContext: 'manager', cargo: cargoGestor, baseRate: comissaoGestorFinal }, commissionRules);
    // Override por produto do Social Seller só pela chave `social_seller` (`commissionRuleCargoKey`).
    // `commissionCargoKey` mapeia "Social Seller" para `sdr`, e a exceção de SDR de um produto
    // (ex.: treinamentos_orbion, sdr 5%) é remuneração da pré-venda, não da coleta do número.
    const comissaoSocialSellerFinal = customComissoes[commissionRuleCargoKey(cargoSocialSeller)] ?? comissaoSocialSeller;
    const policySocialSeller = resolveCommissionPolicy({ ...policyContext, participantContext: 'social_seller', cargo: cargoSocialSeller, baseRate: comissaoSocialSellerFinal }, commissionRules);

    // ⛔ O ponto da venda é emitido UMA vez, na primeira parcela — não uma por parcela.
    //
    // Estes dois objetos só dependem de coisa constante no loop (`vendaData.id`, `dateStr`,
    // `produto`, `paymentCat`, `w`, `feriados`): montá-los dentro do `for` produzia N cópias
    // byte a byte idênticas, com o mesmo `referencia_id`. Uma venda em 12x gerava 12 linhas de
    // comissão pendente carregando o MESMO `pontos_pendentes`, e a RPC `approve_venda` (repo
    // `painel-de-comissoes`) as deduplicava com `ON CONFLICT DO NOTHING` — o desperdício era
    // invisível. Deixou de ser: `inserirComissao` agora grava a linha de ganho 0 quando ela
    // carrega ponto e loga cada uma via RPC `log_app_error`, então uma venda 12x com taxa 0
    // viraria 12 linhas zeradas e 12 round-trips de log. Com o hoist: 1 LINHA por pessoa.
    //
    // O log TAMBÉM caiu para 1 por pessoa (2026-08-27). Ele emitia 13 eventos `comissao-zero`
    // por pessoa numa venda 12x — 39 round-trips sequenciais de `log_app_error` (~7,8 s no
    // submit do operador) contra 2 inserts —, e as parcelas 2..N ainda escreviam "sem ponto a
    // carregar, nada gravado", frase FALSA ali: o ponto existe e foi gravado pela primeira.
    // O `primeira` abaixo é passado adiante e `inserirComissao` só loga quando ele é true.
    // O default do parâmetro é `true`, então os pontos de chamada fora do loop (bônus fixo)
    // continuam logando como antes. Isso reduz ruído, não silencia: o caso segue registrado.
    //
    // Seguro porque `ganho = linha.valor * policy.effectiveRate` com `effectiveRate` constante no
    // loop (resolvida acima, fora dele) e todo `linha.valor > 0` (`entradasParsed` filtra
    // `e.valor > 0`; `valorPorParcela` vem de `valorParcelarNum > 0`). Logo `ganho <= 0` é
    // tudo-ou-nada por pessoa: ou nenhuma parcela insere, ou todas inserem.
    // `semPontoQuandoZerado`: ponto que vale 0 não vira registro (2026-08-28) — ver a nota da
    // função, inclusive a matriz de casos com `inserirComissao`, que não pode quebrar.
    const pontosFechaVenda = semPontoQuandoZerado(vendaData?.id ? {
        acao: 'venda_closer',
        pontos: w.closer[produto]?.[paymentCat] ?? 0,
        referencia_id: vendaData.id,
        referencia_tabela: 'bd_vendas_faturamento',
        data: dateStr,
        observacao: `${dadosVenda.produto || ''} / ${paymentCat}`,
    } : null);

    // Duas guardas independentes, e as duas precisam valer:
    //  - `preVenda.gerar` governa o PONTO porque o ponto de pré-venda só existe dentro da linha
    //    de comissão de pré-venda (`approve_venda` varre `pontos_pendentes` das comissões da
    //    venda). Sem a linha não há veículo — montar o objeto só criaria a ilusão de gravação.
    //  - `semPontoQuandoZerado` continua por fora: ponto que vale 0 não vira registro.
    const pontosPreVenda = semPontoQuandoZerado((preVenda.gerar && vendaData?.id && cargoPontuaAcao(cargoPreVendedor, 'vendaRealizada', w)) ? {
        acao: 'vendaRealizada',
        pontos: pontoDaAcao(cargoPreVendedor, 'vendaRealizada', w, { data: dateStr, feriados }),
        referencia_id: vendaData.id,
        referencia_tabela: 'bd_vendas_faturamento',
        data: dateStr,
    } : null);

    await avisarPreVendaSuprimida({
        preVenda,
        origem: 'submitVenda',
        vendedor: dadosVenda.vendedor,
        preVendedor: dadosVenda.preVendedor,
        cliente: dadosVenda.nome,
        vendaId: vendaData.id,
    });

    // Inserir comissões com status pending — financeiro aprova no painel-de-comissoes
    for (const [iLinha, linha] of linhas.entries()) {
        const primeira = iLinha === 0;

        try {
            await inserirComissao({
                pessoa: dadosVenda.vendedor,
                pessoaId: pessoaVendedor.id,
                tipo: 'Venda',
                cargo: cargoVendedor,
                cargoId: cargoIdVendedor,
                cliente: dadosVenda.nome,
                ganho: linha.valor * policyVendedor.effectiveRate,
                valorBase: linha.valor,
                dataBase: linha.data_venda,
                telefone: normalizePhone(dadosVenda.telefone),
                pontosPendentes: primeira ? pontosFechaVenda : null,
                vendaId: vendaData.id,
                primeira,
            });
        } catch (err) {
            console.error('[inserirComissao failed]', { contexto: 'submitVenda/vendedor', pessoa: dadosVenda.vendedor, cliente: dadosVenda.nome, parcela: linha.parcela }, err);
            throw err;
        }

        if (preVenda.gerar) {
            try {
                await inserirComissao({
                    pessoa: dadosVenda.preVendedor,
                    pessoaId: pessoaPreVendedor.id,
                    tipo: 'Venda (Pré-venda)',
                    cargo: cargoPreVendedor,
                    cargoId: cargoIdPreVendedor,
                    cliente: dadosVenda.nome,
                    ganho: linha.valor * policyPreVendedor.effectiveRate,
                    valorBase: linha.valor,
                    dataBase: linha.data_venda,
                    telefone: normalizePhone(dadosVenda.telefone),
                    pontosPendentes: primeira ? pontosPreVenda : null,
                    vendaId: vendaData.id,
                    primeira,
                });
            } catch (err) {
                console.error('[inserirComissao failed]', { contexto: 'submitVenda/preVendedor', pessoa: dadosVenda.preVendedor, cliente: dadosVenda.nome, parcela: linha.parcela }, err);
                throw err;
            }
        }

        // Sem `pontosPendentes` de propósito: o ponto de venda do SS não existe na config, e o
        // `ON CONFLICT (referencia_id, acao)` de `approve_venda` faria a linha dele colidir com a
        // `vendaRealizada` do SDR na mesma venda. Só dinheiro, por parcela, como as outras.
        if (socialSeller.gerar) {
            try {
                await inserirComissao({
                    pessoa: socialSeller.nome,
                    pessoaId: pessoaSocialSeller.id || socialSeller.pessoaId,
                    tipo: 'Venda (Social Seller)',
                    cargo: cargoSocialSeller,
                    cargoId: cargoIdSocialSeller,
                    cliente: dadosVenda.nome,
                    ganho: linha.valor * policySocialSeller.effectiveRate,
                    valorBase: linha.valor,
                    dataBase: linha.data_venda,
                    telefone: normalizePhone(dadosVenda.telefone),
                    vendaId: vendaData.id,
                    primeira,
                });
            } catch (err) {
                console.error('[inserirComissao failed]', { contexto: 'submitVenda/socialSeller', pessoa: socialSeller.nome, cliente: dadosVenda.nome, parcela: linha.parcela }, err);
                throw err;
            }
        }

        if (nomeGestor) {
            try {
                await inserirComissao({
                    pessoa: nomeGestor,
                    pessoaId: pessoaGestor.id,
                    tipo: 'Venda (Gestão Comercial)',
                    cargo: cargoGestor,
                    cargoId: cargoIdGestor,
                    cliente: dadosVenda.nome,
                    ganho: linha.valor * policyGestor.effectiveRate,
                    valorBase: linha.valor,
                    dataBase: linha.data_venda,
                    telefone: normalizePhone(dadosVenda.telefone),
                    vendaId: vendaData.id,
                    primeira,
                });
            } catch (err) {
                console.error('[inserirComissao failed]', { contexto: 'submitVenda/gestor', pessoa: nomeGestor, cliente: dadosVenda.nome, parcela: linha.parcela }, err);
                throw err;
            }
        }
    }

    await inserirBonusFixoDaVenda({ bonuses: policyVendedor.fixedBonuses, pessoa: dadosVenda.vendedor, pessoaId: pessoaVendedor.id, cargo: cargoVendedor, cargoId: cargoIdVendedor, cliente: dadosVenda.nome, valorBase: valorTotal, dataBase: dateStr, telefone: normalizePhone(dadosVenda.telefone), vendaId: vendaData.id });
    // Mesmo `preVenda.gerar` da comissão: bônus fixo de contexto `pre_sale` é remuneração DO
    // PAPEL de pré-venda. Se o papel não existe nesta venda, ele também não nasce — senão a
    // mesma pessoa receberia o bônus duas vezes pela mesma venda, que é o defeito de 2026-08-31
    // em outra forma. A LÓGICA de bônus/cash collect não foi tocada: `resolveCommissionPolicy`
    // continua idêntica; o que muda é só se o papel participa.
    if (preVenda.gerar) {
        await inserirBonusFixoDaVenda({ bonuses: policyPreVendedor.fixedBonuses, pessoa: dadosVenda.preVendedor, pessoaId: pessoaPreVendedor.id, cargo: cargoPreVendedor, cargoId: cargoIdPreVendedor, cliente: dadosVenda.nome, valorBase: valorTotal, dataBase: dateStr, telefone: normalizePhone(dadosVenda.telefone), vendaId: vendaData.id });
    }
    if (nomeGestor) {
        await inserirBonusFixoDaVenda({ bonuses: policyGestor.fixedBonuses, pessoa: nomeGestor, pessoaId: pessoaGestor.id, cargo: cargoGestor, cargoId: cargoIdGestor, cliente: dadosVenda.nome, valorBase: valorTotal, dataBase: dateStr, telefone: normalizePhone(dadosVenda.telefone), vendaId: vendaData.id });
    }
    if (socialSeller.gerar) {
        await inserirBonusFixoDaVenda({ bonuses: policySocialSeller.fixedBonuses, pessoa: socialSeller.nome, pessoaId: pessoaSocialSeller.id || socialSeller.pessoaId, cargo: cargoSocialSeller, cargoId: cargoIdSocialSeller, cliente: dadosVenda.nome, valorBase: valorTotal, dataBase: dateStr, telefone: normalizePhone(dadosVenda.telefone), vendaId: vendaData.id });
    }

    return { success: true, message: 'Sucesso', rowsGenerated: linhas.length };
};

// Variante de submitVenda para vendas que nascem de um ticket do GHL: não coleta
// lead/produto/vendedor/pré-vendedor de novo (já vêm do ticket, somente-leitura),
// e grava ghl_opportunity_id + fecha o ticket ao final.
export const submitVendaTicket = async ({ ticket, gestorComercial, temGestorComercial, entradas, valorParcelar, parcelas, formaPagamentoParcelas, completadoPor }) => {
    // Checagem no início da função (falha rápido, sem gastar as escritas de comissão,
    // se o ticket já estiver completo)
    const { data: ticketExistente, error: errTicketCheck } = await supabase
        .from('ghl_vendas_tickets')
        .select('status')
        .eq('ghl_opportunity_id', ticket.ghl_opportunity_id)
        .maybeSingle();
    if (errTicketCheck) throw errTicketCheck;
    if (ticketExistente?.status === 'completo') {
        const err = new Error('Este ticket já foi completado por outra pessoa. Atualize a lista.');
        err.code = 'TICKET_JA_COMPLETO';
        throw err;
    }
    const ticketJaExistia = ticketExistente !== null;

    const [pessoaVendedor, pessoaPreVendedor, gestorDaVenda, coletadoSS] = await Promise.all([
        buscarComissaoPessoa(ticket.closer_nome),
        buscarComissaoPessoa(ticket.sdr_nome === 'não identificado' ? null : ticket.sdr_nome),
        resolverGestorDaVenda({ temGestorComercial, gestorComercial }),
        buscarSocialSellerDoLead({ ghlOpportunityId: ticket.ghl_opportunity_id, telefone: ticket.telefone, leadId: ticket.lead_id ?? null }),
    ]);
    // Mesma regra do formulário (ver `socialSeller.js`): a simetria entre os dois caminhos é
    // parte do contrato. Resolvido antes de qualquer escrita.
    const socialSeller = resolveSocialSellerParticipation({
        coletado: coletadoSS,
        sellerId: pessoaVendedor.id,
        sellerName: ticket.closer_nome,
        preSaleId: pessoaPreVendedor.id,
        preSaleName: ticket.sdr_nome === 'não identificado' ? '' : (ticket.sdr_nome || ''),
    });
    const pessoaSocialSeller = await buscarComissaoPessoa(socialSeller.gerar ? socialSeller.nome : null);
    const cargoSocialSeller = pessoaSocialSeller.cargo || 'Social Seller';
    const cargoIdSocialSeller = pessoaSocialSeller.cargo_id || null;
    const comissaoSocialSeller = pessoaSocialSeller.comissao_venda || 0;

    const comissaoVendedor = pessoaVendedor.comissao_venda || 0;
    const cargoVendedor = pessoaVendedor.cargo || 'Closer';
    const cargoIdVendedor = pessoaVendedor.cargo_id || null;
    const gestorEfetivo = resolveCommissionManagerRecipient({
        sellerName: ticket.closer_nome,
        seller: pessoaVendedor,
        managerResolution: gestorDaVenda,
    });
    const pessoaGestor = gestorEfetivo.pessoa;
    const nomeGestor = gestorEfetivo.nome;
    const cargoPreVendedor = pessoaPreVendedor.cargo || '';
    const cargoIdPreVendedor = pessoaPreVendedor.cargo_id || null;
    const cargoGestor = pessoaGestor.cargo || 'Gestor Comercial';
    const cargoIdGestor = pessoaGestor.cargo_id || null;
    // Mesma regra de papel do formulário — a simetria entre os dois caminhos é parte do
    // contrato. `'não identificado'` é o marcador de SDR ausente do ticket, não um nome.
    const nomePreVendedor = ticket.sdr_nome === 'não identificado' ? '' : (ticket.sdr_nome || '');
    const preVenda = resolvePreSaleParticipation({
        sellerName: ticket.closer_nome,
        sellerId: pessoaVendedor.id,
        preSaleName: nomePreVendedor,
        preSaleId: pessoaPreVendedor.id,
    });
    const isSFIndicacao = cargoPreVendedor.trim().toLowerCase() === 'sales farmer' && ticket.sdr_nome !== ticket.closer_nome;
    const comissaoPreVendedor = isSFIndicacao
        ? (pessoaPreVendedor.comissao_indicacao || 0)
        : (pessoaPreVendedor.comissao_venda || 0);
    // Override de gestão: taxa sobre venda de LIDERADO, distinta da taxa de venda própria do
    // gestor. Enquanto o cargo não tiver a taxa separada, buscarComissaoPessoa devolve a de venda
    // própria — o valor pago não muda até a taxa ser configurada.
    const comissaoGestor = pessoaGestor.comissao_venda_override ?? pessoaGestor.comissao_venda ?? 0;

    const entradasParsed = (entradas || [])
        .map(e => ({
            valor: parseFloat(e.valor) || 0,
            formaPagamento: e.formaPagamento || 'Nao Informado',
            data: e.data || hojeSP(),
        }))
        .filter(e => e.valor > 0);

    const valorEntradaTotal = entradasParsed.reduce((s, e) => s + e.valor, 0);
    const valorParcelarNum = parseFloat(valorParcelar) || 0;
    const qtdParcelas = parseInt(parcelas) || 0;
    exigirParcelamentoCompleto({ valorParcelarNum, qtdParcelas, formaPagamentoParcelas });

    const ultimaDataEntrada = entradasParsed.length > 0
        ? [...entradasParsed].map(e => e.data).sort().at(-1)
        : hojeSP();
    const baseDateString = entradasParsed[0]?.data || ultimaDataEntrada;

    const linhas = [];
    entradasParsed.forEach((e, i) => {
        linhas.push({
            parcela: `${i + 1}/${entradasParsed.length}`,
            valor: e.valor,
            forma_pagamento: e.formaPagamento,
            data_venda: e.data,
            tipo_linha: `Entrada/${e.formaPagamento}`,
        });
    });

    if (valorParcelarNum > 0 && qtdParcelas > 0) {
        // O offset só vale para BOLETO. O cartão é um recebimento único, e o dinheiro cai de uma
        // vez — datá-lo um mês à frente só porque houve entrada o tirava do caixa do primeiro mês
        // e matava o cash collect: R$ 5.400 de Pix mais R$ 48.600 no cartão apurava 10% em vez de
        // 100%. Com boleto o offset continua certo: a primeira parcela vence no mês seguinte à
        // entrada, senão duas cobranças cairiam no mesmo mês.
        const offsetBoleto = entradasParsed.length === 0 ? 0 : 1;
        if (formaPagamentoParcelas === 'Cartão de Crédito') {
            linhas.push({
                parcela: `1/${qtdParcelas}`,
                valor: valorParcelarNum,
                forma_pagamento: 'Cartão de Crédito',
                // `ultimaDataEntrada` é a mesma âncora que o código antigo usava (`baseDate`);
                // o que sai é só o `+ offset`.
                data_venda: ultimaDataEntrada,
                tipo_linha: 'Restante/Cartao de Credito',
            });
        } else if (formaPagamentoParcelas === 'Boleto') {
            const valorPorParcela = valorParcelarNum / qtdParcelas;
            for (let i = 1; i <= qtdParcelas; i++) {
                linhas.push({
                    parcela: `${i}/${qtdParcelas}`,
                    valor: valorPorParcela,
                    // `somarMeses` grampeia o dia: `setMonth` transbordava e venda fechada dia 31
                    // pulava meses inteiros. Ver a nota da função em lib/tz.js.
                    data_venda: somarMeses(ultimaDataEntrada, i - 1 + offsetBoleto),
                    tipo_linha: 'Restante/Boleto',
                });
            }
        }
    }

    const valorTotal = valorEntradaTotal + valorParcelarNum;
    const formasPag = [
        ...entradasParsed.map(e => e.formaPagamento),
        ...(valorParcelarNum > 0 && formaPagamentoParcelas ? [formaPagamentoParcelas] : []),
    ];

    const telefoneVenda = normalizePhone(ticket.telefone);
    const emailVenda = null;

    const { data: vendaData, error: errFat } = await supabase.from('bd_vendas_faturamento_pendentes').insert({
        telefone: telefoneVenda,
        nome: ticket.contato_nome,
        email: emailVenda,
        vendedor: ticket.closer_nome,
        vendedor_id: pessoaVendedor.id || null,
        pre_vendedor: ticket.sdr_nome === 'não identificado' ? '' : ticket.sdr_nome,
        pre_vendedor_id: pessoaPreVendedor.id || null,
        produto: ticket.produto || '',
        parcelas: String(parcelas || '1'),
        valor_total: valorTotal,
        valor_entrada: valorEntradaTotal,
        forma_pagamento: formasPag.length > 0 ? formasPag.join('/') : 'Não Informado',
        data_venda: baseDateString || null,
        ghl_opportunity_id: ticket.ghl_opportunity_id,
    }).select('id').single();
    if (errFat) {
        // A checagem do ticket lá em cima é leitura-depois-escrita: dois operadores que abrem o
        // mesmo ticket com segundos de diferença passam os dois por ela, e o segundo só era
        // barrado no fechamento do ticket — depois de faturamento, receita e comissões já
        // gravados em dobro. `bd_vendas_faturamento_pendentes_opp_uidx` (migration
        // 2026-09-02) faz o segundo cair AQUI, na primeira escrita, sem deixar nada órfão.
        // A fila comporta o índice: `approve_venda` e `reject_venda` apagam a linha ao sair.
        if (errFat.code === '23505') {
            const err = new Error('Este ticket já está sendo completado por outra pessoa. Atualize a lista.');
            err.code = 'TICKET_JA_COMPLETO';
            throw err;
        }
        throw errFat;
    }

    for (const linha of linhas) {
        const { error } = await supabase.from('bd_vendas_receita_pendentes').insert({
            telefone: telefoneVenda,
            nome: ticket.contato_nome,
            email: emailVenda,
            vendedor: ticket.closer_nome,
            vendedor_id: pessoaVendedor.id || null,
            pre_vendedor: ticket.sdr_nome === 'não identificado' ? '' : ticket.sdr_nome,
            pre_vendedor_id: pessoaPreVendedor.id || null,
            produto: ticket.produto || '',
            parcela: linha.parcela,
            valor: linha.valor,
            forma_pagamento: linha.forma_pagamento,
            data_venda: linha.data_venda,
            tipo_linha: linha.tipo_linha,
            venda_id: vendaData.id,
        });
        if (error) throw error;
    }

    const dateStr = baseDateString || hojeSP();
    const [{ config: w, feriados }, produtosList, commissionRules] = await Promise.all([
        getWeightsParaData(dateStr),
        getProducts(),
        getCommissionRulesConfig(),
    ]);
    const produto = normalizeProduct(ticket.produto, produtosList);
    const customComissoes = produtosList.find(p => p.slug === produto)?.comissoes ?? {};
    const comissaoVendedorFinal = customComissoes[commissionCargoKey(cargoVendedor)] ?? comissaoVendedor;
    const comissaoPreVendedorFinal = customComissoes[commissionCargoKey(cargoPreVendedor)] ?? comissaoPreVendedor;
    const comissaoGestorFinal = customComissoes[commissionCargoKey(cargoGestor)] ?? comissaoGestor;
    const paymentCat = classifyPaymentLocal(valorEntradaTotal, valorTotal, formaPagamentoParcelas, qtdParcelas);
    const policyContext = {
        productSlug: produto,
        paymentCategory: paymentCat,
        contractTotal: valorTotal,
        firstMonthCash: caixaDoPrimeiroMes(linhas),
        eventDate: dateStr,
    };
    const policyVendedor = resolveCommissionPolicy({ ...policyContext, participantContext: 'seller', cargo: cargoVendedor, baseRate: comissaoVendedorFinal }, commissionRules);
    const policyPreVendedor = resolveCommissionPolicy({ ...policyContext, participantContext: 'pre_sale', cargo: cargoPreVendedor, baseRate: comissaoPreVendedorFinal }, commissionRules);
    const policyGestor = resolveCommissionPolicy({ ...policyContext, participantContext: 'manager', cargo: cargoGestor, baseRate: comissaoGestorFinal }, commissionRules);
    // Override por produto do Social Seller só pela chave `social_seller` (`commissionRuleCargoKey`).
    // `commissionCargoKey` mapeia "Social Seller" para `sdr`, e a exceção de SDR de um produto
    // (ex.: treinamentos_orbion, sdr 5%) é remuneração da pré-venda, não da coleta do número.
    const comissaoSocialSellerFinal = customComissoes[commissionRuleCargoKey(cargoSocialSeller)] ?? comissaoSocialSeller;
    const policySocialSeller = resolveCommissionPolicy({ ...policyContext, participantContext: 'social_seller', cargo: cargoSocialSeller, baseRate: comissaoSocialSellerFinal }, commissionRules);

    // Mesmo hoist de `submitVenda` — ver a justificativa completa lá. A simetria entre os dois
    // caminhos é parte do contrato: uma venda pelo formulário e a mesma venda por ticket do GHL
    // têm que emitir o ponto do mesmo jeito.
    const pontosFechaVenda = semPontoQuandoZerado(vendaData?.id ? {
        acao: 'venda_closer',
        pontos: w.closer[produto]?.[paymentCat] ?? 0,
        referencia_id: vendaData.id,
        referencia_tabela: 'bd_vendas_faturamento',
        data: dateStr,
        observacao: `${ticket.produto || ''} / ${paymentCat}`,
    } : null);

    // Ver a nota em `submitVenda`: sem linha de comissão de pré-venda o ponto não tem veículo,
    // e `semPontoQuandoZerado` continua por fora para o ponto que vale 0 não virar registro.
    const pontosPreVenda = semPontoQuandoZerado((preVenda.gerar && vendaData?.id && cargoPontuaAcao(cargoPreVendedor, 'vendaRealizada', w)) ? {
        acao: 'vendaRealizada',
        pontos: pontoDaAcao(cargoPreVendedor, 'vendaRealizada', w, { data: dateStr, feriados }),
        referencia_id: vendaData.id,
        referencia_tabela: 'bd_vendas_faturamento',
        data: dateStr,
    } : null);

    await avisarPreVendaSuprimida({
        preVenda,
        origem: 'submitVendaTicket',
        vendedor: ticket.closer_nome,
        preVendedor: nomePreVendedor,
        cliente: ticket.contato_nome,
        vendaId: vendaData.id,
    });

    for (const [iLinha, linha] of linhas.entries()) {
        const primeira = iLinha === 0;

        await inserirComissao({
            pessoa: ticket.closer_nome,
            pessoaId: pessoaVendedor.id,
            tipo: 'Venda',
            cargo: cargoVendedor,
            cargoId: cargoIdVendedor,
            cliente: ticket.contato_nome,
            ganho: linha.valor * policyVendedor.effectiveRate,
            valorBase: linha.valor,
            dataBase: linha.data_venda,
            telefone: telefoneVenda,
            pontosPendentes: primeira ? pontosFechaVenda : null,
            vendaId: vendaData.id,
            primeira,
        });

        if (preVenda.gerar) {
            await inserirComissao({
                pessoa: ticket.sdr_nome,
                pessoaId: pessoaPreVendedor.id,
                tipo: 'Venda (Pré-venda)',
                cargo: cargoPreVendedor,
                cargoId: cargoIdPreVendedor,
                cliente: ticket.contato_nome,
                ganho: linha.valor * policyPreVendedor.effectiveRate,
                valorBase: linha.valor,
                dataBase: linha.data_venda,
                telefone: telefoneVenda,
                pontosPendentes: primeira ? pontosPreVenda : null,
                vendaId: vendaData.id,
                primeira,
            });
        }

        // Sem `pontosPendentes`, pelo motivo anotado em `submitVenda`.
        if (socialSeller.gerar) {
            await inserirComissao({
                pessoa: socialSeller.nome,
                pessoaId: pessoaSocialSeller.id || socialSeller.pessoaId,
                tipo: 'Venda (Social Seller)',
                cargo: cargoSocialSeller,
                cargoId: cargoIdSocialSeller,
                cliente: ticket.contato_nome,
                ganho: linha.valor * policySocialSeller.effectiveRate,
                valorBase: linha.valor,
                dataBase: linha.data_venda,
                telefone: telefoneVenda,
                vendaId: vendaData.id,
                primeira,
            });
        }

        if (nomeGestor) {
            await inserirComissao({
                pessoa: nomeGestor,
                pessoaId: pessoaGestor.id,
                tipo: 'Venda (Gestão Comercial)',
                cargo: cargoGestor,
                cargoId: cargoIdGestor,
                cliente: ticket.contato_nome,
                ganho: linha.valor * policyGestor.effectiveRate,
                valorBase: linha.valor,
                dataBase: linha.data_venda,
                telefone: telefoneVenda,
                vendaId: vendaData.id,
                primeira,
            });
        }
    }

    await inserirBonusFixoDaVenda({ bonuses: policyVendedor.fixedBonuses, pessoa: ticket.closer_nome, pessoaId: pessoaVendedor.id, cargo: cargoVendedor, cargoId: cargoIdVendedor, cliente: ticket.contato_nome, valorBase: valorTotal, dataBase: dateStr, telefone: telefoneVenda, vendaId: vendaData.id });
    // Ver a nota em `submitVenda`: bônus de contexto `pre_sale` segue o papel, não a pessoa.
    if (preVenda.gerar) {
        await inserirBonusFixoDaVenda({ bonuses: policyPreVendedor.fixedBonuses, pessoa: ticket.sdr_nome, pessoaId: pessoaPreVendedor.id, cargo: cargoPreVendedor, cargoId: cargoIdPreVendedor, cliente: ticket.contato_nome, valorBase: valorTotal, dataBase: dateStr, telefone: telefoneVenda, vendaId: vendaData.id });
    }
    if (nomeGestor) {
        await inserirBonusFixoDaVenda({ bonuses: policyGestor.fixedBonuses, pessoa: nomeGestor, pessoaId: pessoaGestor.id, cargo: cargoGestor, cargoId: cargoIdGestor, cliente: ticket.contato_nome, valorBase: valorTotal, dataBase: dateStr, telefone: telefoneVenda, vendaId: vendaData.id });
    }
    if (socialSeller.gerar) {
        await inserirBonusFixoDaVenda({ bonuses: policySocialSeller.fixedBonuses, pessoa: socialSeller.nome, pessoaId: pessoaSocialSeller.id || socialSeller.pessoaId, cargo: cargoSocialSeller, cargoId: cargoIdSocialSeller, cliente: ticket.contato_nome, valorBase: valorTotal, dataBase: dateStr, telefone: telefoneVenda, vendaId: vendaData.id });
    }

    // Fecha o ticket. Dois casos, tratados separadamente pra não permitir que uma
    // segunda submissão simultânea sobrescreva silenciosamente a primeira:
    // - Ticket nunca existiu (primeira venda deste opportunity): INSERT puro — a
    //   UNIQUE(ghl_opportunity_id) barra uma segunda tentativa concorrente (erro 23505).
    // - Ticket existe com status='pendente' (reabertura por rejeição): UPDATE
    //   condicional (`.eq('status', 'pendente')`) — se zero linhas forem afetadas,
    //   outra pessoa já completou o mesmo ticket entre a checagem inicial e agora.
    const ticketPayload = {
        status: 'completo',
        venda_faturamento_pendente_id: vendaData.id,
        completado_por: completadoPor,
        completado_em: new Date().toISOString(),
    };

    if (ticketJaExistia) {
        const { data: atualizado, error: errUpdate } = await supabase
            .from('ghl_vendas_tickets')
            .update(ticketPayload)
            .eq('ghl_opportunity_id', ticket.ghl_opportunity_id)
            .eq('status', 'pendente')
            .select('id');
        if (errUpdate) throw errUpdate;
        if (!atualizado || atualizado.length === 0) {
            const err = new Error('Este ticket já foi completado por outra pessoa. Atualize a lista.');
            err.code = 'TICKET_JA_COMPLETO';
            throw err;
        }
    } else {
        const { error: errInsert } = await supabase
            .from('ghl_vendas_tickets')
            .insert({ ghl_opportunity_id: ticket.ghl_opportunity_id, ...ticketPayload });
        if (errInsert) {
            if (errInsert.code === '23505') {
                const err = new Error('Este ticket já foi completado por outra pessoa. Atualize a lista.');
                err.code = 'TICKET_JA_COMPLETO';
                throw err;
            }
            throw errInsert;
        }
    }

    return { success: true, message: 'Sucesso', rowsGenerated: linhas.length };
};

const mergeTexto = (existente, novo) => {
    if (!existente) return novo || '';
    if (!novo) return existente;
    return `${existente}\n---\n${novo}`;
};

export const submitDiario = async (dadosDiario) => {
    const { data: existentes, error: errBusca } = await supabase
        .from('bd_diarios_sdr')
        .select('*')
        .eq('data', dadosDiario.data)
        .eq('nome', dadosDiario.nome)
        .limit(1);
    if (errBusca) throw errBusca;

    if (!existentes || existentes.length === 0) {
        const { error } = await supabase.from('bd_diarios_sdr').insert({
            data: dadosDiario.data,
            nome: dadosDiario.nome,
            pessoa_id: await resolvePessoaId(dadosDiario.nome),
            recebidos: dadosDiario.recebidos,
            respondidos: dadosDiario.respondidos,
            invalidos: dadosDiario.invalidos,
            desqualificados: dadosDiario.desqualificados,
            aprendizados: dadosDiario.aprendizados,
            desafios: dadosDiario.desafios || '',
        });
        if (error) throw error;
    } else {
        const ex = existentes[0];
        const { error } = await supabase
            .from('bd_diarios_sdr')
            .update({
                recebidos: ex.recebidos + dadosDiario.recebidos,
                respondidos: ex.respondidos + dadosDiario.respondidos,
                invalidos: ex.invalidos + dadosDiario.invalidos,
                desqualificados: ex.desqualificados + dadosDiario.desqualificados,
                aprendizados: mergeTexto(ex.aprendizados, dadosDiario.aprendizados),
                desafios: mergeTexto(ex.desafios, dadosDiario.desafios),
            })
            .eq('id', ex.id);
        if (error) throw error;
    }

    return { success: true };
};

// Sales Farmer: papel full-cycle. Diário dedicado de 4 campos em bd_diarios_sales_farmer.
// Upsert idempotente por (data, nome), espelhando submitDiario/submitDiarioCloser. Ver ADR-0002.
export const submitDiarioSalesFarmer = async (dadosDiario) => {
    try {
        const { data: existentes, error: errBusca } = await supabase
            .from('bd_diarios_sales_farmer')
            .select('*')
            .eq('data', dadosDiario.data)
            .eq('nome', dadosDiario.nome)
            .limit(1);
        if (errBusca) { await logError(errBusca, { fn: 'submitDiarioSalesFarmer/busca' }); throw errBusca; }

        if (!existentes || existentes.length === 0) {
            const { error } = await supabase.from('bd_diarios_sales_farmer').insert({
                data: dadosDiario.data || null,
                nome: dadosDiario.nome || '',
                pessoa_id: await resolvePessoaId(dadosDiario.nome),
                abordados: dadosDiario.abordados || 0,
                respondidos: dadosDiario.respondidos || 0,
                aprendizados: dadosDiario.aprendizados || '',
                desafios: dadosDiario.desafios || '',
            });
            if (error) { await logError(error, { fn: 'submitDiarioSalesFarmer/insert' }); throw error; }
        } else {
            const ex = existentes[0];
            const { error } = await supabase
                .from('bd_diarios_sales_farmer')
                .update({
                    abordados: ex.abordados + dadosDiario.abordados,
                    respondidos: ex.respondidos + dadosDiario.respondidos,
                    aprendizados: mergeTexto(ex.aprendizados, dadosDiario.aprendizados),
                    desafios: mergeTexto(ex.desafios, dadosDiario.desafios),
                })
                .eq('id', ex.id);
            if (error) { await logError(error, { fn: 'submitDiarioSalesFarmer/update' }); throw error; }
        }

        return { success: true };
    } catch (error) {
        await logError(error, { fn: 'submitDiarioSalesFarmer' });
        throw error;
    }
};

export const submitDiarioSocialSeller = async (dadosDiario) => {
    const { data: existentes, error: errBusca } = await supabase
        .from('bd_diarios_social_seller')
        .select('*')
        .eq('data', dadosDiario.data)
        .eq('nome', dadosDiario.nome)
        .limit(1);
    if (errBusca) throw errBusca;

    if (!existentes || existentes.length === 0) {
        const { error } = await supabase.from('bd_diarios_social_seller').insert({
            data: dadosDiario.data,
            nome: dadosDiario.nome,
            pessoa_id: await resolvePessoaId(dadosDiario.nome),
            abordagens_novos_contatos: dadosDiario.abordagens_novos_contatos,
            retorno: dadosDiario.retorno,
            follow_up: dadosDiario.follow_up,
            aprendizados: dadosDiario.aprendizados,
            desafios: dadosDiario.desafios || '',
        });
        if (error) throw error;
    } else {
        const ex = existentes[0];
        const { error } = await supabase
            .from('bd_diarios_social_seller')
            .update({
                abordagens_novos_contatos: ex.abordagens_novos_contatos + dadosDiario.abordagens_novos_contatos,
                retorno: ex.retorno + dadosDiario.retorno,
                follow_up: (ex.follow_up || 0) + dadosDiario.follow_up,
                aprendizados: mergeTexto(ex.aprendizados, dadosDiario.aprendizados),
                desafios: mergeTexto(ex.desafios, dadosDiario.desafios),
            })
            .eq('id', ex.id);
        if (error) throw error;
    }

    return { success: true };
};

export const submitDiarioCloser = async (dadosDiario) => {
    const { data: existentes, error: errBusca } = await supabase
        .from('bd_diarios_closer')
        .select('*')
        .eq('data', dadosDiario.data)
        .eq('nome', dadosDiario.nome)
        .limit(1);
    if (errBusca) throw errBusca;

    if (!existentes || existentes.length === 0) {
        const { error } = await supabase.from('bd_diarios_closer').insert({
            data: dadosDiario.data || null,
            nome: dadosDiario.nome || '',
            pessoa_id: await resolvePessoaId(dadosDiario.nome),
            indicacoes_coletadas: dadosDiario.indicacoes_coletadas || 0,
            aprendizados: dadosDiario.aprendizados || '',
            desafios: dadosDiario.desafios || '',
        });
        if (error) throw error;
    } else {
        const ex = existentes[0];
        const { error } = await supabase
            .from('bd_diarios_closer')
            .update({
                indicacoes_coletadas: (ex.indicacoes_coletadas || 0) + (dadosDiario.indicacoes_coletadas || 0),
                aprendizados: mergeTexto(ex.aprendizados, dadosDiario.aprendizados),
                desafios: mergeTexto(ex.desafios, dadosDiario.desafios),
            })
            .eq('id', ex.id);
        if (error) throw error;
    }

    return { success: true };
};

export const submitEstrelas = async (telefone, estrelas, numero_reuniao, tipo_reuniao) => {
    const telClean = normalizePhone(telefone);
    const estrelasInt = parseInt(estrelas) || 0;

    // Buscar agendamento para obter id, agendador, cargo e data. A reunião ainda
    // não concluída é selecionada por:
    // 1. data_reuniao <= hoje — exclui reuniões futuras
    // 2. tipo_reuniao IS NULL — só a reunião que ainda não recebeu a realização
    // 3. rodada: mira a numero_reuniao EXATA reportada (R1/R2/R3…). Como a rodada é
    //    definida no agendamento e o relatório/calendário a repassa, mirar nela permite
    //    concluir Continuação (R2+) sem atualizar a linha R1 errada do mesmo telefone.
    //    Sem rodada (chamadas legadas): mantém o comportamento antigo (só R1, exclui Continuação).
    const hoje = hojeSP();
    let busca = supabase
        .from('bd_agendamentos')
        .select('id, agendador, agendador_id, cargo, data_agendamento')
        .eq('telefone', telClean)
        .lte('data_reuniao', hoje)
        .is('tipo_reuniao', null);
    busca = numero_reuniao
        ? busca.eq('numero_reuniao', numero_reuniao)
        : busca.neq('tipo_agendamento', 'Continuação da Agenda');
    const { data: agendList, error: errBusca } = await busca
        .order('data_reuniao', { ascending: false })
        .limit(1);
    if (errBusca) return { success: false, error: errBusca.message };
    if (!agendList || agendList.length === 0) return { success: false, error: 'Telefone nao encontrado nos agendamentos' };

    const agend = agendList[0];

    // numero_reuniao (rodada) já vem do agendamento — não sobrescrever aqui.
    // A conclusão marca a realização via tipo_reuniao (+ estrelas no R1).
    const updatePayload = { estrelas: estrelasInt };
    if (tipo_reuniao) updatePayload.tipo_reuniao = tipo_reuniao;

    const { data: updated, error } = await supabase
        .from('bd_agendamentos')
        .update(updatePayload)
        .eq('id', agend.id)
        .select('id');
    if (error) return { success: false, error: error.message };
    if (!updated || updated.length === 0) return { success: false, error: 'Atualização não aplicada (verifique permissões da tabela)' };

    // Atualizar ledger de pontos — config-driven (cargo × ação × valor), com o peso da época
    const dataPonto = agend.data_agendamento;
    const { config: w, feriados } = await getWeightsParaData(dataPonto);
    const cargo = agend.cargo || '';

    const podeRealizada = cargoPontuaAcao(cargo, 'agendaRealizada', w);
    const podeEstrela = cargoPontuaAcao(cargo, 'estrelaBonus', w);
    if (estrelasInt > 0 && (podeRealizada || podeEstrela)) {
        const agendadorPontoId = agend.agendador_id || await resolvePessoaId(agend.agendador);
        const rows = [];
        if (podeRealizada) rows.push({
            pessoa: agend.agendador, pessoa_id: agendadorPontoId, cargo,
            acao: 'agendaRealizada', pontos: pontoDaAcao(cargo, 'agendaRealizada', w, { data: dataPonto, feriados }),
            referencia_id: agend.id, referencia_tabela: 'bd_agendamentos', data: dataPonto,
        });
        if (podeEstrela) rows.push({
            pessoa: agend.agendador, pessoa_id: agendadorPontoId, cargo,
            acao: 'estrelaBonus', pontos: arredondaPontos(estrelasInt * pontoDaAcao(cargo, 'estrelaBonus', w, { data: dataPonto, feriados })),
            referencia_id: agend.id, referencia_tabela: 'bd_agendamentos', data: dataPonto,
        });
        await supabase.from('bd_pontos').upsert(rows, { onConflict: 'referencia_id,acao', ignoreDuplicates: false })
            .then(({ error }) => { if (error) throw new Error(`[bd_pontos] ${error.message}`); });
    } else {
        // Estrelas zeradas (ou cargo sem essas ações): remover pontos relacionados
        await supabase.from('bd_pontos')
            .delete()
            .eq('referencia_id', agend.id)
            .in('acao', ['agendaRealizada', 'estrelaBonus']);
    }

    return { success: true };
};

export const submitConclusaoReuniao = async (agendamentoId, { closerNome, tipo_reuniao, estrelas, aprendizados, desafios }) => {
    // Busca agendamento para obter data, agendador e cargo (necessários para pontos e diário)
    const { data: agList, error: errAg } = await supabase
        .from('bd_agendamentos')
        .select('id, agendador, agendador_id, cargo, data_reuniao, telefone')
        .eq('id', agendamentoId)
        .limit(1);
    if (errAg) { await logError(errAg, { fn: 'submitConclusaoReuniao/lookup', agendamentoId }); return { success: false, error: errAg.message }; }
    if (!agList?.length) return { success: false, error: 'Agendamento não encontrado' };
    const ag = agList[0];

    const dataRef = ag.data_reuniao || hojeSP();

    // Upsert em bd_diarios_closer (mesma lógica de submitDiarioCloser, r1_realizadas sempre +1)
    const { data: existentes, error: errBusca } = await supabase
        .from('bd_diarios_closer')
        .select('*')
        .eq('data', dataRef)
        .eq('nome', closerNome)
        .limit(1);
    if (errBusca) { await logError(errBusca, { fn: 'submitConclusaoReuniao/diario-busca' }); return { success: false, error: errBusca.message }; }

    if (!existentes?.length) {
        const { error } = await supabase.from('bd_diarios_closer').insert({
            data: dataRef,
            nome: closerNome,
            pessoa_id: await resolvePessoaId(closerNome),
            r1_realizadas: 1,
            aprendizados: aprendizados || '',
            desafios: desafios || '',
        });
        if (error) { await logError(error, { fn: 'submitConclusaoReuniao/diario-insert' }); return { success: false, error: error.message }; }
    } else {
        const ex = existentes[0];
        const { error } = await supabase
            .from('bd_diarios_closer')
            .update({
                r1_realizadas: ex.r1_realizadas + 1,
                aprendizados: mergeTexto(ex.aprendizados, aprendizados),
                desafios: mergeTexto(ex.desafios, desafios),
            })
            .eq('id', ex.id);
        if (error) { await logError(error, { fn: 'submitConclusaoReuniao/diario-update' }); return { success: false, error: error.message }; }
    }

    // Update bd_agendamentos: realização = tipo_reuniao (+ estrelas no R1).
    // numero_reuniao (rodada) já vem do agendamento — não sobrescrever.
    const estrelasInt = parseInt(estrelas) || 0;
    const updatePayload = { estrelas: estrelasInt };
    if (tipo_reuniao) updatePayload.tipo_reuniao = tipo_reuniao;
    const { error: errUpd } = await supabase.from('bd_agendamentos').update(updatePayload).eq('id', agendamentoId);
    if (errUpd) { await logError(errUpd, { fn: 'submitConclusaoReuniao/ag-update' }); return { success: false, error: errUpd.message }; }

    // Upsert pontos — config-driven (cargo × ação × valor)
    const cargoConc = ag.cargo || '';
    const dataConc = ag.data_reuniao || dataRef;
    const { config: wConc, feriados: feriadosConc } = await getWeightsParaData(dataConc);
    const podeRealizadaConc = cargoPontuaAcao(cargoConc, 'agendaRealizada', wConc);
    const podeEstrelaConc = cargoPontuaAcao(cargoConc, 'estrelaBonus', wConc);
    if (estrelasInt > 0 && (podeRealizadaConc || podeEstrelaConc)) {
        const agendadorPontoId = ag.agendador_id || await resolvePessoaId(ag.agendador);
        const rows = [];
        if (podeRealizadaConc) rows.push({
            pessoa: ag.agendador, pessoa_id: agendadorPontoId, cargo: ag.cargo,
            acao: 'agendaRealizada', pontos: pontoDaAcao(cargoConc, 'agendaRealizada', wConc, { data: dataConc, feriados: feriadosConc }),
            referencia_id: ag.id, referencia_tabela: 'bd_agendamentos', data: dataConc,
        });
        if (podeEstrelaConc) rows.push({
            pessoa: ag.agendador, pessoa_id: agendadorPontoId, cargo: ag.cargo,
            acao: 'estrelaBonus', pontos: arredondaPontos(estrelasInt * pontoDaAcao(cargoConc, 'estrelaBonus', wConc, { data: dataConc, feriados: feriadosConc })),
            referencia_id: ag.id, referencia_tabela: 'bd_agendamentos', data: dataConc,
        });
        await supabase.from('bd_pontos').upsert(rows, { onConflict: 'referencia_id,acao', ignoreDuplicates: false }).then(({ error }) => {
            if (error) throw new Error(`[bd_pontos] ${error.message}`);
        });
    } else if (estrelasInt === 0 && (podeRealizadaConc || podeEstrelaConc)) {
        await supabase.from('bd_pontos').delete().eq('referencia_id', ag.id).in('acao', ['agendaRealizada', 'estrelaBonus']);
    }

    return { success: true };
};

export const verificarTelefoneAgendamento = async (telefone) => {
    const canonical = normalizePhone(telefone);
    const { data, error } = await supabase
        .from('bd_agendamentos')
        .select('id, nome, email, empresa, nicho, fonte, numero_reuniao, tipo_reuniao')
        .eq('telefone', canonical)
        .order('created_at', { ascending: false })
        .limit(1);
    if (error) return { existe: false, error: error.message };
    if (!data || data.length === 0) return { existe: false };
    const lead = data[0];
    return {
        existe: true, nome: lead.nome || '', email: lead.email || '', empresa: lead.empresa || '',
        nicho: lead.nicho || '', fonte: lead.fonte || '',
        numero_reuniao: lead.numero_reuniao || '', tipo_reuniao: lead.tipo_reuniao || '',
    };
};

export const salvarPessoa = async (dadosPessoa) => {
    invalidatePessoaIdCache(); // pessoa nova/renomeada/removida → força refetch do map nome→id
    const { acao, nome, nomeOriginal, cargo, cargo_id, comissao_agendamento, comissao_venda, comissao_indicacao, email, atividade, ghl_user_id } = dadosPessoa;
    const nomeTrimmed = nome ? nome.trim() : '';
    const nomeOriginalTrimmed = nomeOriginal ? nomeOriginal.trim() : nomeTrimmed;
    const emailTrimmed = email ? email.trim().toLowerCase() : null;
    const atividadeFinal = atividade || 'ativo';
    const ghlUserIdFinal = ghl_user_id ? ghl_user_id.trim() : null;

    // Legacy comissao columns: only write when explicitly provided in payload.
    // New Pessoas.jsx (cargo_id flow) omits them → skip to avoid zeroing fallback data.
    // Old callers (direct API, painel) may still supply them.
    const hasLegacyComissao = comissao_agendamento !== undefined || comissao_venda !== undefined || comissao_indicacao !== undefined;
    const legacyComissaoPatch = hasLegacyComissao ? {
        comissao_agendamento: parseFloat(comissao_agendamento) || 0,
        comissao_venda: (parseFloat(comissao_venda) || 0) / 100,
        comissao_indicacao: (parseFloat(comissao_indicacao) || 0) / 100,
    } : {};

    if (acao === 'excluir') {
        // Soft-delete: marca inativo em vez de DELETE. Preserva histórico (salários,
        // comissões, promoções) e evita bloqueio por FK NO ACTION (bd_comissoes,
        // bd_comissoes_pendentes, ss_contatos_coletados). Reversível via edição.
        const { data: upd, error } = await supabase
            .from('bd_pessoas')
            .update({ atividade: 'inativo' })
            .eq('nome', nomeOriginalTrimmed)
            .select('id');
        if (error) { await logError(error, { fn: 'salvarPessoa/excluir', nome: nomeOriginalTrimmed }); throw error; }
        if (!upd || upd.length === 0) {
            const e = new Error(`Nenhuma pessoa encontrada com o nome "${nomeOriginalTrimmed}".`);
            await logError(e, { fn: 'salvarPessoa/excluir', nome: nomeOriginalTrimmed });
            throw e;
        }
        return { success: true, removed: upd.length };
    }

    if (acao === 'editar') {
        const { error } = await supabase
            .from('bd_pessoas')
            .update({
                nome: nomeTrimmed,
                cargo,
                cargo_id: cargo_id || null,
                ...legacyComissaoPatch, // omitted when not in payload (PR 8 removes these cols)
                email: emailTrimmed,
                atividade: atividadeFinal,
                ghl_user_id: ghlUserIdFinal,
            })
            .eq('nome', nomeOriginalTrimmed);
        if (error) throw error;
        return { success: true };
    }

    const { error } = await supabase
        .from('bd_pessoas')
        .insert({ nome: nomeTrimmed, cargo, cargo_id: cargo_id || null, ...legacyComissaoPatch, email: emailTrimmed, atividade: atividadeFinal, ghl_user_id: ghlUserIdFinal });
    if (error) throw error;
    return { success: true };
};

export const promoverPessoa = async ({ pessoaId, novoCargoId, motivo, autorEmail }) => {
    // RPC executa log + update numa transação única — sem risco de log fantasma
    const { data, error } = await supabase.rpc('promover_pessoa', {
        p_pessoa_id:    pessoaId,
        p_novo_cargo_id: novoCargoId,
        p_motivo:       motivo || null,
        p_autor_email:  autorEmail || null,
    });
    if (error) { logError(error, { fn: 'promoverPessoa', pessoaId, novoCargoId }); throw error; }
    return data ?? { success: true };
};

export const getGHLSources = async () => {
    try {
        const response = await fetch('/api/ghl-sources');
        if (!response.ok) {
            logError(new Error(`GHL sources HTTP ${response.status}`), { fn: 'getGHLSources', severity: 'warning' });
            return [];
        }
        const data = await response.json();
        return data.sources || [];
    } catch (error) {
        logError(error, { fn: 'getGHLSources', severity: 'warning' });
        return [];
    }
};

// ── Configurações globais (app_settings) ─────────────────────────────────────

export const getAppSetting = async (key) => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', key)
        .single();
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found
    return data?.value ?? null;
};

export const setAppSetting = async (key, value) => {
    const { error } = await supabase
        .from('app_settings')
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
    if (error) throw error;
};

// Fonte única: ghl_reunioes (GHL-first). Filtra pela DATA DO AGENDAMENTO
// (agendada_em/agendado_em_evento), não por created_at — o created_at do ghl_reunioes
// é o momento da captura/reprojeção, não a data comercial do agendamento.
export const getHistoricoAgendamentos = async (dataInicio, dataFim, filtroAgendador = '', filtroFonte = '', filtroTipo = '') => {
    const { data: alias } = await supabase.from('ghl_pessoa_alias').select('ghl_user_id, pessoa_nome');
    const nomePorUser = new Map((alias || []).filter(a => a.ghl_user_id).map(a => [a.ghl_user_id, a.pessoa_nome]));
    const { data, error } = await supabase.from('ghl_reunioes')
        .select('id, ghl_opportunity_id, evento_origem_id, created_at, agendada_em, agendado_em_evento, data_reuniao, sdr_nome, sdr_ghl_user_id, contato_nome, telefone_canonico, tipo_agendamento, fonte, campos_pendentes');
        // Sem filtro de source_system: ghl_reunioes é a tabela única e contém tanto os
        // eventos reais do GHL (source='ghl') quanto o histórico importado (source='desk_legacy').
        // O Registro de Eventos mostra os dois (a data real vem de agendada_em||agendado_em_evento).
    if (error) throw error;
    let rows = (data || []).map(r => adaptReuniaoParaAgendamento(r, nomePorUser))
        .filter(r => r.data_agendamento && r.data_agendamento >= dataInicio && r.data_agendamento <= dataFim);
    if (filtroAgendador) rows = rows.filter(r => r.agendador === filtroAgendador);
    if (filtroFonte) rows = rows.filter(r => r.fonte === filtroFonte);
    if (filtroTipo) rows = rows.filter(r => r.tipo_agendamento === filtroTipo);
    rows.sort((a, b) => (b.data_agendamento || '').localeCompare(a.data_agendamento || ''));
    return rows;
};

export const getOpcoesAgendamentos = async (dataInicio, dataFim) => {
    const { data, error } = await supabase.from('ghl_reunioes')
        .select('fonte, tipo_agendamento, agendada_em, agendado_em_evento');
    if (error) throw error;
    const inRange = (r) => {
        const d = (r.agendada_em || r.agendado_em_evento || '').slice(0, 10);
        return d && d >= dataInicio && d <= dataFim;
    };
    const rows = (data || []).filter(inRange);
    const fontes = [...new Set(rows.map(r => r.fonte).filter(Boolean))].sort();
    const tipos = [...new Set(rows.map(r => r.tipo_agendamento).filter(Boolean))].sort();
    return { fontes, tipos };
};

// Fonte única: ghl_vendas (GHL-first). Filtra pela DATA DA VENDA (vendido_em).
export const getHistoricoVendas = async (dataInicio, dataFim, filtroVendedor = '') => {
    const { data: alias } = await supabase.from('ghl_pessoa_alias').select('ghl_user_id, pessoa_nome');
    const nomePorUser = new Map((alias || []).filter(a => a.ghl_user_id).map(a => [a.ghl_user_id, a.pessoa_nome]));
    const { data, error } = await supabase.from('ghl_vendas')
        .select('id, ghl_opportunity_id, evento_origem_id, created_at, vendido_em, contato_nome, telefone_canonico, closer_ghl_user_id, sdr_ghl_user_id, produto, valor_venda, forma_pagamento, status');
    if (error) { logError(error, { fn: 'getHistoricoVendas' }); throw error; }
    let rows = (data || []).map(v => adaptVenda(v, nomePorUser))
        .filter(r => r.data_venda && r.data_venda >= dataInicio && r.data_venda <= dataFim);
    if (filtroVendedor) rows = rows.filter(r => r.vendedor === filtroVendedor);
    rows.sort((a, b) => (b.data_venda || '').localeCompare(a.data_venda || ''));
    return rows;
};

export const getAgendamentosParaCalendario = async (dataInicio, dataFim) => {
    // Fonte nova: ghl_reunioes (estrutura GHL). SDR/closer resolvidos por ghl_user_id via alias
    // quando o *_nome vier vazio. Comparecimento vem do pipeline do GHL (true/false/null).
    const [reunioesRes, aliasRes] = await Promise.all([
        supabase
            .from('ghl_reunioes')
            .select('id, contato_nome, telefone_canonico, sdr_nome, sdr_ghl_user_id, closer_nome, closer_ghl_user_id, tipo_agendamento, data_reuniao, hora_reuniao, estrelas, tipo_reuniao, seq, comparecimento, campos_pendentes, ghl_opportunity_id')
            .gte('data_reuniao', dataInicio)
            .lte('data_reuniao', dataFim)
            .not('data_reuniao', 'is', null)
            .order('data_reuniao', { ascending: true })
            .order('hora_reuniao', { ascending: true, nullsFirst: false }),
        supabase.from('ghl_pessoa_alias').select('ghl_user_id, pessoa_nome'),
    ]);
    if (reunioesRes.error) {
        await logError(reunioesRes.error, { fn: 'getAgendamentosParaCalendario' });
        throw reunioesRes.error;
    }
    const alias = new Map((aliasRes.data || []).map(a => [a.ghl_user_id, a.pessoa_nome]));
    const nomeDe = (nome, userId) => nome || (userId ? (alias.get(userId) || '') : '');
    return (reunioesRes.data || []).map(r => ({
        id: r.id,
        nome: r.contato_nome || '',
        telefone: r.telefone_canonico || '',
        agendador: nomeDe(r.sdr_nome, r.sdr_ghl_user_id),
        closer_assigned: nomeDe(r.closer_nome, r.closer_ghl_user_id),
        tipo_agendamento: r.tipo_agendamento || '',
        data_reuniao: r.data_reuniao ? String(r.data_reuniao).slice(0, 10) : '',
        horario_reuniao: r.hora_reuniao || '',
        estrelas: r.estrelas || 0,
        tipo_reuniao: r.tipo_reuniao || '',
        numero_reuniao: r.seq ? `R${r.seq}` : '',
        comparecimento: r.comparecimento,
        campos_pendentes: r.campos_pendentes === true,
        ghl_opportunity_id: r.ghl_opportunity_id || null,
    }));
};

// Atualiza o closer responsável de um agendamento já criado.
// Usado pelo SDR (agendador) para reatribuir o closer pelo popup do calendário.
export const updateCloserAssigned = async (agendamentoId, closerNome) => {
    const { error } = await supabase
        .from('bd_agendamentos')
        .update({ closer_assigned: closerNome, closer_id: await resolvePessoaId(closerNome) })
        .eq('id', agendamentoId);
    if (error) {
        await logError(error, { fn: 'updateCloserAssigned', agendamentoId });
        throw error;
    }
    return { success: true };
};

export const getRegistroEnviosDiarios = async (dataInicio, dataFim) => {
    const [sdrRes, ssRes, closerRes, farmerRes] = await Promise.all([
        supabase.from('bd_diarios_sdr')
            .select('data, nome, created_at')
            .gte('data', dataInicio)
            .lte('data', dataFim),
        supabase.from('bd_diarios_social_seller')
            .select('data, nome, created_at')
            .gte('data', dataInicio)
            .lte('data', dataFim),
        supabase.from('bd_diarios_closer')
            .select('data, nome, created_at')
            .gte('data', dataInicio)
            .lte('data', dataFim),
        supabase.from('bd_diarios_sales_farmer')
            .select('data, nome, created_at')
            .gte('data', dataInicio)
            .lte('data', dataFim),
    ]);
    if (sdrRes.error) throw sdrRes.error;
    if (ssRes.error) throw ssRes.error;
    if (closerRes.error) throw closerRes.error;
    if (farmerRes.error) throw farmerRes.error;

    const entries = [
        ...(sdrRes.data || []).map(r => ({ ...r, tipo: 'SDR' })),
        ...(ssRes.data || []).map(r => ({ ...r, tipo: 'Social Seller' })),
        ...(closerRes.data || []).map(r => ({ ...r, tipo: 'Closer' })),
        ...(farmerRes.data || []).map(r => ({ ...r, tipo: 'Sales Farmer' })),
    ];
    entries.sort((a, b) => b.data.localeCompare(a.data) || a.nome.localeCompare(b.nome));
    return entries;
};

// ── Números coletados (ss_contatos_coletados) — leitura e edição pela aba Eventos ──
export const getContatosColetados = async (dataInicio, dataFim) => {
    let q = supabase
        .from('ss_contatos_coletados')
        .select('id, data, social_seller, social_seller_id, telefone_canonico, fonte, sdr_destino, lead_id, ghl_opportunity_id, leads(nome)')
        .is('deleted_at', null) // soft-delete: esconde os apagados pelo desk
        .order('data', { ascending: false });
    if (dataInicio) q = q.gte('data', dataInicio);
    if (dataFim) q = q.lte('data', dataFim);
    const { data, error } = await q;
    if (error) { logError(error, { fn: 'getContatosColetados' }); throw error; }
    const rows = (data ?? []).map(r => ({ ...r, lead_nome: r.leads?.nome ?? null }));

    // Fallback: coletados sem nome via lead_id (lead_id ausente ou lead sem nome)
    // tentam casar por telefone_canonico na tabela leads.
    const tels = [...new Set(rows.filter(r => !r.lead_nome && r.telefone_canonico).map(r => r.telefone_canonico))];
    if (tels.length) {
        const { data: porTel } = await supabase
            .from('leads')
            .select('nome, telefone_canonico')
            .in('telefone_canonico', tels);
        const mapTel = new Map((porTel ?? []).filter(l => l.nome).map(l => [l.telefone_canonico, l.nome]));
        rows.forEach(r => { if (!r.lead_nome && r.telefone_canonico) r.lead_nome = mapTel.get(r.telefone_canonico) ?? null; });
    }

    // NOTA: o nome/contato do GHL para os coletados sem nome local é resolvido em
    // BACKGROUND pela aba Eventos (getGhlContatoInfo), fora do caminho de carregamento —
    // a busca no GHL leva ~30s e travava a aba inteira quando ficava aqui.
    return rows;
};

// Resolve nome + contactId no GHL (server-side) para uma lista de telefones.
// Chamada em background pela aba Eventos — NÃO usar no caminho de carregamento inicial.
// Retorna { telefone: { nome, id } } (só os que o GHL encontrou).
export const getGhlContatoInfo = async (telefones) => {
    const tels = [...new Set((telefones || []).filter(Boolean))];
    if (!tels.length) return {};
    try {
        const resp = await fetch('/api/ghl-contact-names', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ telefones: tels }),
        });
        if (!resp.ok) return {};
        const { contatos } = await resp.json();
        return contatos || {};
    } catch { return {}; } // GHL indisponível: aba mantém telefone como fallback
};

export const deleteContatoColetado = async (id) => {
    // Soft-delete: marca deleted_at em vez de remover. A linha precisa continuar existindo
    // para o sync (contato-bridge) não recriar o coletado — ele deduplica por evento_origem_id
    // checando se a linha existe. Um DELETE físico faria o coletado voltar no próximo ciclo.
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from('ss_contatos_coletados').update({ deleted_at: nowIso }).eq('id', id);
    if (error) { logError(error, { fn: 'deleteContatoColetado', id }); throw error; }
    // Cancela pontos e comissões do evento para sair do ranking/pagamento. Não-bloqueante:
    // a linha já saiu da aba; falha aqui é logada mas não reverte o soft-delete.
    const { error: ePt } = await supabase.from('bd_pontos')
        .update({ canceled_at: nowIso })
        .eq('referencia_tabela', 'ss_contatos_coletados').eq('referencia_id', id).is('canceled_at', null);
    if (ePt) logError(ePt, { fn: 'deleteContatoColetado/pontos', id });
    const { error: eCm } = await supabase.from('bd_comissoes')
        .update({ canceled_at: nowIso })
        .eq('referencia_tabela', 'ss_contatos_coletados').eq('referencia_id', id).is('canceled_at', null);
    if (eCm) logError(eCm, { fn: 'deleteContatoColetado/comissoes', id });
    return { success: true };
};

// ── Leads criados via Cadastro de Leads (bd_leads_desk) — tipo "Lead criado" na aba Eventos ──
export const getLeadsDesk = async (dataInicio, dataFim) => {
    let q = supabase
        .from('bd_leads_desk')
        .select('id, data, nome, telefone, email, fonte, registrado_por, ghl_contact_id, ghl_opportunity_id')
        .order('data', { ascending: false });
    if (dataInicio) q = q.gte('data', dataInicio);
    if (dataFim) q = q.lte('data', dataFim);
    const { data, error } = await q;
    if (error) { logError(error, { fn: 'getLeadsDesk' }); return []; } // tolerante: não quebra a aba Eventos
    return data ?? [];
};

export const updateLeadDesk = async (id, patch) => {
    const CAMPOS = ['data', 'nome', 'telefone', 'email', 'fonte', 'registrado_por'];
    const upd = {};
    for (const k of CAMPOS) if (k in patch) upd[k] = patch[k];
    const { error } = await supabase.from('bd_leads_desk').update(upd).eq('id', id);
    if (error) { logError(error, { fn: 'updateLeadDesk', id }); throw error; }
    return { success: true };
};

export const deleteLeadDesk = async (id) => {
    const { error } = await supabase.from('bd_leads_desk').delete().eq('id', id);
    if (error) { logError(error, { fn: 'deleteLeadDesk', id }); throw error; }
    return { success: true };
};

export const updateContatoColetado = async (id, patch) => {
    const CAMPOS = ['data', 'social_seller', 'social_seller_id', 'telefone_canonico', 'fonte'];
    const upd = {};
    for (const k of CAMPOS) if (k in patch) upd[k] = patch[k];
    const { error } = await supabase.from('ss_contatos_coletados').update(upd).eq('id', id);
    if (error) { logError(error, { fn: 'updateContatoColetado', id }); throw error; }
    return { success: true };
};

// ── Dashboard Comercial (somente leitura) ────────────────────────────────────

// Reuniões realizadas = agendamentos concluídos (numero_reuniao preenchido por
// submitConclusaoReuniao/submitEstrelas). Filtra por data_reuniao no intervalo.
export const getReunioesRealizadas = async (dataInicio, dataFim) => {
    const { data, error } = await supabase
        .from('bd_agendamentos')
        .select('id, nome, telefone, agendador, closer_assigned, data_reuniao, horario_reuniao, tipo_reuniao, numero_reuniao, estrelas, fonte')
        .gte('data_reuniao', dataInicio)
        .lte('data_reuniao', dataFim)
        .not('numero_reuniao', 'is', null)
        .order('data_reuniao', { ascending: false })
        .order('horario_reuniao', { ascending: true, nullsFirst: false });
    if (error) {
        await logError(error, { fn: 'getReunioesRealizadas' });
        throw error;
    }
    return data || [];
};

// Anotações = aprendizados/desafios não vazios registrados nos diários no período.
// Espelha o Promise.all de getRegistroEnviosDiarios.
export const getAnotacoesDiarios = async (dataInicio, dataFim) => {
    const [sdrRes, ssRes, closerRes, farmerRes] = await Promise.all([
        supabase.from('bd_diarios_sdr')
            .select('data, nome, aprendizados, desafios, created_at')
            .gte('data', dataInicio).lte('data', dataFim),
        supabase.from('bd_diarios_social_seller')
            .select('data, nome, aprendizados, desafios, created_at')
            .gte('data', dataInicio).lte('data', dataFim),
        supabase.from('bd_diarios_closer')
            .select('data, nome, aprendizados, desafios, created_at')
            .gte('data', dataInicio).lte('data', dataFim),
        supabase.from('bd_diarios_sales_farmer')
            .select('data, nome, aprendizados, desafios, created_at')
            .gte('data', dataInicio).lte('data', dataFim),
    ]);
    if (sdrRes.error) { await logError(sdrRes.error, { fn: 'getAnotacoesDiarios/sdr' }); throw sdrRes.error; }
    if (ssRes.error) { await logError(ssRes.error, { fn: 'getAnotacoesDiarios/ss' }); throw ssRes.error; }
    if (closerRes.error) { await logError(closerRes.error, { fn: 'getAnotacoesDiarios/closer' }); throw closerRes.error; }
    if (farmerRes.error) { await logError(farmerRes.error, { fn: 'getAnotacoesDiarios/farmer' }); throw farmerRes.error; }

    const hasNota = (r) => (r.aprendizados && r.aprendizados.trim()) || (r.desafios && r.desafios.trim());
    const entries = [
        ...(sdrRes.data || []).map(r => ({ ...r, tipo: 'SDR' })),
        ...(ssRes.data || []).map(r => ({ ...r, tipo: 'Social Seller' })),
        ...(closerRes.data || []).map(r => ({ ...r, tipo: 'Closer' })),
        ...(farmerRes.data || []).map(r => ({ ...r, tipo: 'Sales Farmer' })),
    ].filter(hasNota);
    entries.sort((a, b) => b.data.localeCompare(a.data) || a.nome.localeCompare(b.nome));
    return entries;
};

// ── Hub Sources (lead-hub-orbion) ────────────────────────────────────────────

export const getHubSources = async () => {
    const { data, error } = await supabase.rpc('get_hub_sources_for_sdr');
    if (error) {
        await logError(error, { fn: 'getHubSources' });
        throw error;
    }
    return data || [];
};

export const updateHubSourceSDRConfig = async (sourceId, { sdr_distribution_enabled, sdr_assignments, fallback_sdr }) => {
    const { error } = await supabase.rpc('update_hub_source_sdr_config', {
        p_id: sourceId,
        p_routing: {
            sdr_distribution_enabled,
            sdr_assignments,
            fallback_sdr: fallback_sdr || null,
        },
    });
    if (error) {
        await logError(error, { fn: 'updateHubSourceSDRConfig', sourceId });
        throw error;
    }
};

export const getHubSdrAssignmentsRecent = async (sourceSlug, limit = 50) => {
    const { data, error } = await supabase.rpc('get_hub_sdr_assignments_recent', {
        p_source_slug: sourceSlug,
        p_limit: limit,
    });
    if (error) {
        await logError(error, { fn: 'getHubSdrAssignmentsRecent', sourceSlug });
        throw error;
    }
    return data || [];
};

export const getHubSdrState = async (sourceSlug) => {
    const { data, error } = await supabase.rpc('get_hub_sdr_state', {
        p_source_slug: sourceSlug,
    });
    if (error) {
        await logError(error, { fn: 'getHubSdrState', sourceSlug });
        throw error;
    }
    return (data && data[0]) || null;
};

// --- Cadastro de Leads ---

export const getGHLPipelines = async () => {
    const res = await fetch('/api/ghl-pipelines');
    if (!res.ok) throw new Error(`getGHLPipelines: ${res.status}`);
    const data = await res.json();
    return data.pipelines || [];
};

const LEAD_SOURCES_CONFIG_KEY = 'lead_sources_ghl_config';
const GHL_SOURCES_LIST_KEY = 'ghl_sources_list';

export const getGHLSourcesList = async () => {
    const val = await getAppSetting(GHL_SOURCES_LIST_KEY);
    return Array.isArray(val) ? val.sort() : [];
};

export const setGHLSourcesList = async (sources) => {
    return setAppSetting(GHL_SOURCES_LIST_KEY, sources);
};

export const getLeadSourcesConfig = async () => {
    return getAppSetting(LEAD_SOURCES_CONFIG_KEY);
};

export const setLeadSourcesConfig = async (config) => {
    return setAppSetting(LEAD_SOURCES_CONFIG_KEY, config);
};

export const submitCadastroLead = async ({ nome, telefone, email, fonte, registradoPor, observacoes }) => {
    const res = await fetch('/api/ghl-create-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome, telefone, email, fonte, registradoPor, observacoes }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail ? `${data.error} (${data.detail})` : (data.error || `submitCadastroLead: ${res.status}`));
    return data;
};

// ── Central de Controle: CRUD (editar/apagar) ───────────────────────────────────

/**
 * Deleta um registro de ghl_reunioes ou ghl_vendas.
 * Se evento_origem_id for informado, deleta também o evento em ghl_eventos.
 * Escreve pelo cliente autenticado (sessão do usuário); depende das policies de RLS
 * que permitem UPDATE/DELETE a usuários autenticados nestas tabelas.
 */
export const deleteRegistro = async ({ tabela, id, evento_origem_id, ghl_opportunity_id }) => {
    try {
        // Validar tabela
        if (!['ghl_reunioes', 'ghl_vendas'].includes(tabela)) {
            throw new Error(`Tabela inválida: ${tabela}`);
        }

        // Delete registro principal
        const { error: errDel } = await supabase
            .from(tabela)
            .delete()
            .eq('id', id);
        if (errDel) {
            await logError(errDel, { fn: 'deleteRegistro/main', tabela, id });
            throw errDel;
        }

        // Delete evento de origem (se informado)
        if (evento_origem_id) {
            const { error: errEvento } = await supabase
                .from('ghl_eventos')
                .delete()
                .eq('id', evento_origem_id);
            if (errEvento) {
                // Log mas não lança — evento pode não existir
                await logError(errEvento, { fn: 'deleteRegistro/evento', evento_origem_id, severity: 'warning' });
            }
        }

        // Venda: limpa também o ticket (ghl_vendas_tickets) para não ficar órfão.
        if (tabela === 'ghl_vendas' && ghl_opportunity_id) {
            const { error: errTk } = await supabase
                .from('ghl_vendas_tickets')
                .delete()
                .eq('ghl_opportunity_id', ghl_opportunity_id);
            if (errTk) await logError(errTk, { fn: 'deleteRegistro/ticket', ghl_opportunity_id, severity: 'warning' });
        }

        return { success: true };
    } catch (error) {
        await logError(error, { fn: 'deleteRegistro', tabela, id });
        throw error;
    }
};

/**
 * Apaga por completo um ticket/venda GHL identificado pelo ghl_opportunity_id:
 * a(s) venda(s), o ticket e os eventos de venda do opp. Usado na aba de tickets pendentes
 * (ação de gestor). Tickets pendentes ainda não geraram comissão, então não há o que estornar.
 */
export const deleteTicketVenda = async ({ ghl_opportunity_id }) => {
    if (!ghl_opportunity_id) throw new Error('ghl_opportunity_id obrigatório.');
    try {
        const { error: eEv } = await supabase.from('ghl_eventos').delete()
            .eq('ghl_opportunity_id', ghl_opportunity_id).in('evento', ['venda', 'venda_perdida']);
        if (eEv) await logError(eEv, { fn: 'deleteTicketVenda/eventos', ghl_opportunity_id, severity: 'warning' });
        const { error: eTk } = await supabase.from('ghl_vendas_tickets').delete().eq('ghl_opportunity_id', ghl_opportunity_id);
        if (eTk) await logError(eTk, { fn: 'deleteTicketVenda/ticket', ghl_opportunity_id, severity: 'warning' });
        const { error: eVd } = await supabase.from('ghl_vendas').delete().eq('ghl_opportunity_id', ghl_opportunity_id);
        if (eVd) { await logError(eVd, { fn: 'deleteTicketVenda/venda', ghl_opportunity_id }); throw eVd; }
        return { success: true };
    } catch (error) {
        await logError(error, { fn: 'deleteTicketVenda', ghl_opportunity_id });
        throw error;
    }
};

/**
 * Colunas `timestamptz` que o operador preenche escolhendo um DIA (`<input type="date">`).
 * Sem âncora, o Postgres resolve `'2026-09-01'` na timezone da sessão (UTC no PostgREST) e grava
 * meia-noite UTC — que lida em `America/Sao_Paulo` é o dia ANTERIOR. Espelha
 * `COLUNAS_DATA_TIMESTAMPTZ` de `dashboard-orbion/src/lib/ghl/field-extract.ts`, que aplica a
 * mesma âncora no caminho do GHL. Coluna `date` pura (`data_reuniao`) NÃO entra aqui: dia sem
 * hora não tem fuso, e ancorá-la seria inventar um instante.
 */
const COLUNAS_DIA_EM_TIMESTAMPTZ = new Set(['agendada_em', 'vendido_em']);

/**
 * Atualiza campos editáveis de um registro em ghl_reunioes ou ghl_vendas.
 * Whitelist de campos por tabela.
 * Escreve pelo cliente autenticado (sessão do usuário); depende das policies de RLS
 * que permitem UPDATE/DELETE a usuários autenticados nestas tabelas.
 */
export const updateRegistro = async ({ tabela, id, patch }) => {
    try {
        // Validar tabela
        if (!['ghl_reunioes', 'ghl_vendas'].includes(tabela)) {
            throw new Error(`Tabela inválida: ${tabela}`);
        }

        // Whitelist de campos editáveis por tabela — painel de controle TOTAL:
        // data/hora da reunião, data do agendamento, SDR e closer (nome + ghl_user_id),
        // tipo, fonte, comparecimento e briefing.
        const whitelistPorTabela = {
            ghl_reunioes: [
                'data_reuniao',
                'hora_reuniao',
                'agendada_em',          // data do agendamento
                'sdr_nome',             // o SDR (rótulo)
                'sdr_ghl_user_id',      // o SDR (id GHL, fonte da resolução de nome)
                'closer_nome',
                'closer_ghl_user_id',
                'tipo_agendamento',
                'fonte',
                'comparecimento',
                'tipo_reuniao',
                'reuniao_completa',     // R1 em que o closer também apresentou → "Reunião Completa" no dashboard
                'campos_pendentes',
                'descricao',
            ],
            ghl_vendas: [
                'valor_venda',
                'produto',
                'forma_pagamento',
                'status',
                'vendido_em',           // data da venda
                'closer_nome',
                'closer_ghl_user_id',
                'sdr_nome',
                'sdr_ghl_user_id',
                'parcelas',
                'descricao',
            ],
        };

        const whitelist = whitelistPorTabela[tabela] || [];
        const patchFiltrado = {};

        // Filtrar apenas campos permitidos
        for (const [key, value] of Object.entries(patch || {})) {
            if (whitelist.includes(key)) {
                patchFiltrado[key] = COLUNAS_DIA_EM_TIMESTAMPTZ.has(key) ? ancoraMeioDiaSP(value) : value;
            }
        }

        // Precedência do Desk: ao marcar comparecimento manualmente, trava contra sobrescrita
        // pelo sync do GHL (dashboard lê comparecimento_manual e não reescreve o comparecimento).
        if (tabela === 'ghl_reunioes' && Object.prototype.hasOwnProperty.call(patchFiltrado, 'comparecimento')) {
            patchFiltrado.comparecimento_manual = true;
        }

        // Se nenhum campo válido, retornar sucesso silencioso
        if (Object.keys(patchFiltrado).length === 0) {
            return { success: true, updated: false };
        }

        // Update
        const { data: updated, error } = await supabase
            .from(tabela)
            .update(patchFiltrado)
            .eq('id', id)
            .select('id');

        if (error) {
            await logError(error, { fn: 'updateRegistro', tabela, id });
            throw error;
        }

        if (!updated || updated.length === 0) {
            return { success: true, updated: false };
        }

        return { success: true, updated: true };
    } catch (error) {
        await logError(error, { fn: 'updateRegistro', tabela, id });
        throw error;
    }
};

// ── Central de Controle: Pontos & Comissões vinculados ──────────────────────────
// O desk é o escritor legítimo de bd_pontos/bd_comissoes. A remoção é SEMPRE suave
// (canceled_at): o dashboard filtra `canceled_at IS NULL` no ranking (bd_pontos) e
// nos pagamentos (bd_comissoes), então cancelar tira do ranking/pagamento sem perder
// a auditoria. Escritas pelo cliente autenticado (sessão do usuário) + RLS que permite
// INSERT/UPDATE a autenticados (bd_pontos) e UPDATE (bd_comissoes).

const PONTO_CAMPOS = ['pessoa', 'pessoa_id', 'cargo', 'acao', 'pontos', 'data', 'observacao'];
const COMISSAO_CAMPOS = ['pessoa', 'pessoa_id', 'cargo', 'tipo', 'ganho', 'valor_base', 'data_base', 'mes_pagamento', 'status'];

/**
 * Busca a linha crua e completa de ghl_reunioes/ghl_vendas por id — o modal de
 * edição precisa dos valores reais (hora_reuniao, sdr_nome, agendada_em,
 * comparecimento, descricao, valor_venda…) que o objeto adaptado da lista não traz.
 */
export const getRegistroCompleto = async ({ tabela, id }) => {
    if (!['ghl_reunioes', 'ghl_vendas'].includes(tabela) || !id) return null;
    const { data, error } = await supabase.from(tabela).select('*').eq('id', id).maybeSingle();
    if (error) { await logError(error, { fn: 'getRegistroCompleto', tabela, id }); throw error; }
    return data || null;
};

/**
 * Colunas da trava manual, criadas por `supabase/migration-2026-08-25-pontos-trava-manual.sql`.
 *
 * O desk deploya sozinho, por push; a migration é ato manual do dono. Entre um e outro existe uma
 * janela em que o código já pede `travado_em` e o banco ainda não tem a coluna — e aí o PostgREST
 * recusa a leitura (42703) e a escrita (PGRST204) inteiras. Sem degradar, essa janela deixaria o
 * operador PIOR do que antes da trava: antes ele editava e perdia em 60s, aqui não conseguiria
 * nem editar nem ver a lista de pontos. Por isso o primeiro erro de coluna ausente vira um
 * fallback sem carimbo — e a tela (`PontoRow`) avisa que a proteção não está de pé.
 */
const TRAVA_COLUNAS = 'travado_em, travado_por';

/** null = ainda não exercitado nesta sessão; true = colunas existem; false = migration pendente. */
let travaSuportada = null;

/** Lido pela UI para não prometer proteção que o banco ainda não sustenta. */
export const travaDeEdicaoDisponivel = () => travaSuportada;

/**
 * Decide se o erro é "culpa das colunas da trava".
 *
 * Classifica pelo **nome da coluna**, não pelo código: hoje o PostgREST devolve `42703` no select
 * e `PGRST204` no update, mas o texto muda com a versão e não deu para observar o erro real
 * contra produção. A assimetria decide o critério — errar para o lado estrito (não reconhecer)
 * quebra TODA edição de ponto, que é justamente o que a degradação existe para evitar; errar para
 * o lado frouxo (reconhecer um erro de permissão *nessas colunas*) grava sem carimbo, loga com
 * `degradado: 'trava'` e a tela passa a mostrar "Sem trava". Fica visível, não silencioso.
 *
 * Erro que não cita as colunas — permissão na tabela, rede, constraint — continua estourando:
 * ali fingir que salvou seria pior do que falhar.
 */
const ehTravaAusente = (error) => {
    if (!error) return false;
    const texto = `${error.message || ''} ${error.details || ''} ${error.hint || ''} ${error.code || ''}`;
    return /travado_em|travado_por/.test(texto);
};

const PONTO_COLUNAS = 'id, pessoa, pessoa_id, cargo, acao, pontos, data, observacao, referencia_tabela, referencia_id, created_at';

/**
 * Lista os pontos (bd_pontos) vinculados a uma reunião/venda via
 * referencia_tabela + referencia_id. Exclui cancelados.
 */
export const getPontosVinculados = async ({ tabela, referencia_id }) => {
    if (!referencia_id || !['ghl_reunioes', 'ghl_vendas', 'ss_contatos_coletados'].includes(tabela)) return [];
    const consultar = (colunas) => supabase
        .from('bd_pontos')
        .select(colunas)
        .eq('referencia_tabela', tabela)
        .eq('referencia_id', referencia_id)
        .is('canceled_at', null)
        .order('created_at', { ascending: true });

    if (travaSuportada !== false) {
        const { data, error } = await consultar(`${PONTO_COLUNAS}, ${TRAVA_COLUNAS}`);
        if (!error) { travaSuportada = true; return data || []; }
        if (!ehTravaAusente(error)) { await logError(error, { fn: 'getPontosVinculados', tabela, referencia_id }); throw error; }
        travaSuportada = false;
        await logError(error, { fn: 'getPontosVinculados', tabela, referencia_id, degradado: 'trava' });
    }
    // Migration pendente: lista sem as colunas da trava em vez de derrubar o painel inteiro.
    const { data, error } = await consultar(PONTO_COLUNAS);
    if (error) { await logError(error, { fn: 'getPontosVinculados', tabela, referencia_id }); throw error; }
    return data || [];
};

/**
 * Lista as comissões (bd_comissoes) de um lead/venda. Casa por venda_id (se houver)
 * ou pelo telefone canônico (55+DDD+número). Exclui cancelados.
 */
export const getComissoesVinculadas = async ({ telefone, data, tabela, referencia_id }) => {
    const telCanon = telefone ? normalizePhone(telefone) : null;
    const sel = 'id, pessoa, pessoa_id, cargo, tipo, cliente, ganho, valor_base, data_base, mes_pagamento, telefone, venda_id, status, referencia_tabela, referencia_id, created_at';
    const out = new Map();
    // 1) Exato pelo ID do evento (referência) — fonte de verdade (Opção A).
    if (referencia_id && tabela) {
        const { data: exatas, error } = await supabase.from('bd_comissoes')
            .select(sel).eq('referencia_tabela', tabela).eq('referencia_id', referencia_id).is('canceled_at', null);
        if (error) { await logError(error, { fn: 'getComissoesVinculadas/ref', tabela, referencia_id }); throw error; }
        (exatas || []).forEach(c => out.set(c.id, c));
    }
    // 2) Fallback (comissões antigas sem referência): telefone + data do evento.
    // Some conforme o backfill for carimbando o referencia_id nas históricas.
    if (telCanon && data) {
        const { data: legadas, error } = await supabase.from('bd_comissoes')
            .select(sel).eq('telefone', telCanon).eq('data_base', data).is('referencia_id', null).is('canceled_at', null);
        if (error) { await logError(error, { fn: 'getComissoesVinculadas/fallback', telefone: telCanon }); throw error; }
        (legadas || []).forEach(c => out.set(c.id, c));
    }
    return [...out.values()].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
};

// `CREATE UNIQUE INDEX ... ON bd_comissoes (lead_id) WHERE tipo = 'Agendamento'`. O nome do
// índice é o que o Postgres devolve dentro da mensagem do 23505, e é por ele que a comissão
// manual distingue "este lead já recebeu" de qualquer outra violação de unicidade.
const UIDX_AGENDAMENTO_POR_LEAD = 'bd_comissoes_um_agendamento_por_lead_uidx';

/**
 * Cria uma comissão manual vinculada a um evento (carimba referencia_tabela/referencia_id).
 *
 * `tipo` cai em 'Agendamento' por padrão e o chamador é a tela de Registro de Eventos, que
 * mostra `e.message` cru para o operador. Com o índice no ar, lançar a segunda comissão de
 * Agendamento do mesmo lead devolve 23505 — a duplicata mais recente de produção (2026-08-26)
 * saiu dessa tela. Por isso o erro é traduzido abaixo em vez de vazar o texto do Postgres.
 */
export const addComissaoManual = async ({ telefone, data_base, pessoa, pessoa_id, cargo, tipo, ganho, referencia_tabela, referencia_id }) => {
    if (!pessoa || ganho == null || ganho === '') throw new Error('Pessoa e ganho são obrigatórios.');
    const telCanon = telefone ? normalizePhone(telefone) : null;
    const base = data_base || hojeSP();
    const row = {
        pessoa,
        pessoa_id: pessoa_id || await resolvePessoaId(pessoa),
        cargo: cargo || null,
        tipo: tipo || 'Agendamento',
        ganho: Number(ganho),
        data_base: base,
        mes_pagamento: calcularMesPagamento(base),
        telefone: telCanon,
        status: 'approved',
        created_by: 'desk-central',
        referencia_tabela: referencia_tabela || null,
        referencia_id: referencia_id || null,
    };
    const { data: inserted, error } = await supabase.from('bd_comissoes').insert(row).select('id');
    if (error) {
        await logError(error, { fn: 'addComissaoManual' });
        // Traduz SÓ o 23505 desta constraint; qualquer outro erro sobe como está — inclusive
        // outro 23505, que vem de outra regra e pede outra instrução. O log acima guarda o erro
        // cru, então traduzir não custa observabilidade. Mesmo padrão de `month_lock_closed`
        // em submitAgendamento.
        if (error.code === '23505' && error.message?.includes(UIDX_AGENDAMENTO_POR_LEAD)) {
            throw new Error('Este lead já tem uma comissão de Agendamento. A regra é 1 por lead na vida — edite a existente em vez de criar outra.');
        }
        throw error;
    }
    return { success: true, id: inserted?.[0]?.id };
};

/** Mapa ghl_user_id -> pessoa_nome (ghl_pessoa_alias) para resolver SDR/closer no modal. */
export const getGhlPessoaAliasMap = async () => {
    const { data } = await supabase.from('ghl_pessoa_alias').select('ghl_user_id, pessoa_nome');
    return new Map((data || []).filter(a => a.ghl_user_id).map(a => [a.ghl_user_id, a.pessoa_nome]));
};

/**
 * Identificação do operador da sessão, para carimbar quem travou a linha.
 * Nunca lança: sem sessão resolvida a trava continua valendo (quem decide é `travado_em`),
 * só perde a autoria.
 */
async function identificarOperador() {
    try {
        const { data: { user } } = await supabase.auth.getUser();
        return user?.email || user?.id || 'desk';
    } catch {
        return 'desk';
    }
}

/**
 * G2 — recusa trocar a `acao` de uma linha que um escritor automático ainda reconcilia.
 *
 * A trava protege a LINHA, não o par `(referencia_id, acao)` que ela ocupa. Trocar a ação libera
 * o par: no ciclo seguinte o sweep recria a ação original ao lado da linha editada e a reunião
 * passa a valer o dobro. `travado_em` não impede isso — a linha editada é justamente a que sai
 * do filtro do escritor.
 *
 * Mora aqui, e não só em `PontoRow`, porque a tela é uma das entradas de `updatePonto`, não a
 * única: guarda que só existe no JSX não vale para console, script ou tela nova. A regra em si
 * está em `motivoBloqueioTrocaAcao` (pura, testada) — aqui só se busca o estado real da linha.
 *
 * Lê `referencia_tabela, acao` (colunas que existem desde sempre, fora da degradação da trava) e
 * só quando o patch mexe na ação — edição de valor, o caso comum, não paga roundtrip nenhum.
 * Se a leitura falhar, RECUSA: criar uma linha fantasma no ranking é pior que recusar um salvar.
 */
async function recusarTrocaDeAcaoReconciliada(id, acaoNova) {
    const { data, error } = await supabase
        .from('bd_pontos')
        .select('referencia_tabela, acao')
        .eq('id', id)
        .maybeSingle();
    if (error) {
        await logError(error, { fn: 'updatePonto/guardaTrocaAcao', id });
        throw new Error('Não deu para confirmar a ação atual deste ponto — a troca de ação foi recusada. Tente de novo.');
    }
    if (!data) return; // linha some entre o carregamento e o salvar: o update não acha nada e é no-op.
    if ((data.acao || '') === (acaoNova || '')) return;
    const bloqueio = motivoBloqueioTrocaAcao(data);
    if (!bloqueio) return;
    throw new Error(`${bloqueio.resumo} ${bloqueio.detalhe}`);
}

/**
 * Atualiza campos editáveis de um ponto (bd_pontos). Restrito por whitelist.
 *
 * Editar TRAVA a linha (`travado_em`/`travado_por`), e a trava é carimbada aqui, nunca vinda do
 * patch: `pontos-bridge.ts` é um sweep que roda a cada 60s (pg_cron `sync-ghl-prod-1min`) e faz
 * upsert com `onConflict: 'referencia_id,acao'`. A constraint não é parcial, então o ON CONFLICT
 * cai justamente na linha editada e devolve o valor calculado — medido em produção em 2026-08-25,
 * a edição estava de pé em t+30s e revertida em t+60s. A trava é o que faz o bridge pular a linha.
 *
 * A trava vai em coluna dedicada e não em `observacao`, porque o próprio upsert reescreve
 * `observacao` (`pontos-bridge.ts:200`).
 *
 * Enquanto a migration da trava não estiver aplicada, grava só o patch e devolve
 * `travaIndisponivel: true` — editar continua possível, sem proteção. Ver `TRAVA_COLUNAS`.
 */
export const updatePonto = async ({ id, patch }) => {
    const patchFiltrado = {};
    for (const [k, v] of Object.entries(patch || {})) {
        if (PONTO_CAMPOS.includes(k)) patchFiltrado[k] = v;
    }
    if (!id || Object.keys(patchFiltrado).length === 0) return { success: true, updated: false };
    if ('acao' in patchFiltrado) await recusarTrocaDeAcaoReconciliada(id, patchFiltrado.acao);
    const gravar = (payload) => supabase.from('bd_pontos').update(payload).eq('id', id);

    if (travaSuportada !== false) {
        const travado_em = new Date().toISOString();
        const travado_por = await identificarOperador();
        const { error } = await gravar({ ...patchFiltrado, travado_em, travado_por });
        if (!error) { travaSuportada = true; return { success: true, updated: true, travado_em, travado_por }; }
        if (!ehTravaAusente(error)) { await logError(error, { fn: 'updatePonto', id }); throw error; }
        travaSuportada = false;
        await logError(error, { fn: 'updatePonto', id, degradado: 'trava' });
    }
    // Migration pendente: salva o valor sem carimbo. Volta a ser sobrescrito pelo sweep em até
    // 1 min — a tela diz isso antes de o operador clicar em Salvar, não é perda silenciosa.
    const { error } = await gravar(patchFiltrado);
    if (error) { await logError(error, { fn: 'updatePonto', id }); throw error; }
    return { success: true, updated: true, travado_em: null, travado_por: null, travaIndisponivel: true };
};

/**
 * Devolve a linha ao cálculo automático: limpa a trava e o bridge volta a mandar nela no
 * próximo ciclo (inclusive podendo apagá-la, se a reunião não gerar mais aquela ação).
 */
export const destravarPonto = async ({ id }) => {
    if (!id) return { success: false };
    // Sem as colunas não existe linha travada para soltar: nada a fazer, e não é erro.
    if (travaSuportada === false) return { success: true, travaIndisponivel: true };
    const { error } = await supabase
        .from('bd_pontos')
        .update({ travado_em: null, travado_por: null })
        .eq('id', id);
    if (error) {
        if (ehTravaAusente(error)) {
            travaSuportada = false;
            await logError(error, { fn: 'destravarPonto', id, degradado: 'trava' });
            return { success: true, travaIndisponivel: true };
        }
        await logError(error, { fn: 'destravarPonto', id });
        throw error;
    }
    return { success: true };
};

/** Cancela (soft-delete) um ponto — sai do ranking (dashboard filtra canceled_at IS NULL). */
export const cancelarPonto = async ({ id }) => {
    if (!id) return { success: false };
    const { error } = await supabase.from('bd_pontos').update({ canceled_at: new Date().toISOString() }).eq('id', id);
    if (error) { await logError(error, { fn: 'cancelarPonto', id }); throw error; }
    return { success: true };
};

/**
 * Cria um ponto manual vinculado a uma reunião/venda. observacao='desk-manual'.
 *
 * Nasce **travado**, pelo mesmo motivo da edição: se a ação for uma das que o sweep gera para
 * aquela mesma referência, o upsert por `(referencia_id, acao)` cai nesta linha e devolve o valor
 * calculado no ciclo seguinte. `observacao='desk-manual'` protege só do **delete** de
 * reconciliação, que filtra por `observacao` — o upsert não filtra.
 */
export const addPontoManual = async ({ tabela, referencia_id, pessoa, pessoa_id, cargo, acao, pontos, data, observacao }) => {
    if (!pessoa || pontos == null || pontos === '') throw new Error('Pessoa e pontos são obrigatórios.');
    const row = {
        pessoa,
        pessoa_id: pessoa_id || await resolvePessoaId(pessoa),
        cargo: cargo || null,
        acao: acao || 'manual',
        pontos: Number(pontos),
        data: data || hojeSP(),
        referencia_tabela: tabela || null,
        referencia_id: referencia_id || null,
        observacao: observacao || 'desk-manual',
    };
    const inserir = (carimbo) => supabase.from('bd_pontos').insert({ ...row, ...carimbo }).select('id');

    if (travaSuportada !== false) {
        const carimbo = { travado_em: new Date().toISOString(), travado_por: await identificarOperador() };
        const { data: inserted, error } = await inserir(carimbo);
        if (!error) { travaSuportada = true; return { success: true, id: inserted?.[0]?.id, ...carimbo }; }
        if (!ehTravaAusente(error)) { await logError(error, { fn: 'addPontoManual' }); throw error; }
        travaSuportada = false;
        await logError(error, { fn: 'addPontoManual', degradado: 'trava' });
    }
    // Migration pendente: cria sem carimbo (a tela avisa) em vez de impedir de lançar o ponto.
    const { data: inserted, error } = await inserir();
    if (error) { await logError(error, { fn: 'addPontoManual' }); throw error; }
    return { success: true, id: inserted?.[0]?.id, travaIndisponivel: true };
};

/**
 * Atualiza campos editáveis de uma comissão (bd_comissoes). Restrito por whitelist.
 *
 * ⛔ `mes_pagamento` É DERIVADO, não digitado (2026-09-01). `ComissaoRow` (`RegistroEventos.jsx`)
 * deixa o operador editar `data_base` e `ganho` e nunca mandou `mes_pagamento` — então a
 * competência de pagamento ficava congelada no valor calculado na CRIAÇÃO. Medido em produção:
 * a comissão do lead Daniel HONSHO nasceu com `data_base` 2026-10-01 (a SDR digitou o mês
 * errado) e `mes_pagamento` 2026-11-15; a data foi corrigida para 2026-09-01 e o pagamento
 * continuou marcado para novembro, dois meses depois do devido.
 *
 * A regra é a mesma da criação (`calcularMesPagamento`: data_base + 1 mês, dia 15) e a mesma da
 * ponte do dashboard (`ghl/commission.ts:mesPagamentoAgend`). Recalcular em toda edição também
 * RECONCILIA linhas que já estavam divergentes: tocar no valor de uma comissão torta conserta a
 * competência dela de passagem.
 *
 * Duas saídas deliberadas:
 * - `mes_pagamento` explícito no patch VENCE. Quem manda a competência na mão está decidindo,
 *   não esquecendo — é o caminho de corrigir um caso fora da regra.
 * - Se o valor derivado for igual ao que já está lá, a coluna não entra no patch. Escrever o
 *   mesmo valor acordaria `tg_comissoes_block_closed_month` à toa em mês fechado.
 */
export const updateComissao = async ({ id, patch }) => {
    const patchFiltrado = {};
    for (const [k, v] of Object.entries(patch || {})) {
        if (COMISSAO_CAMPOS.includes(k)) patchFiltrado[k] = v;
    }
    if (!id || Object.keys(patchFiltrado).length === 0) return { success: true, updated: false };

    const mexeNaBase = 'data_base' in patchFiltrado || 'ganho' in patchFiltrado;
    if (mexeNaBase && !('mes_pagamento' in patchFiltrado)) {
        // Só lê o banco quando precisa: `data_base` fora do patch (editaram apenas o ganho) ou
        // `mes_pagamento` atual, para não reescrever o que já está certo.
        const { data: atual, error: erroLeitura } = await supabase
            .from('bd_comissoes').select('data_base, mes_pagamento').eq('id', id).single();
        if (erroLeitura) { await logError(erroLeitura, { fn: 'updateComissao/derivarMes', id }); throw erroLeitura; }
        const base = String(patchFiltrado.data_base ?? atual?.data_base ?? '').slice(0, 10);
        // Sem `data_base` não há de onde derivar. Preservar o que está lá é melhor do que
        // gravar a competência de hoje sobre uma comissão cuja base ninguém sabe qual é.
        if (base) {
            const derivado = calcularMesPagamento(base);
            if (derivado !== String(atual?.mes_pagamento ?? '').slice(0, 10)) patchFiltrado.mes_pagamento = derivado;
        }
    }

    const { error } = await supabase.from('bd_comissoes').update(patchFiltrado).eq('id', id);
    if (error) {
        await logError(error, { fn: 'updateComissao', id });
        // A competência derivada pode cair num mês que o financeiro já fechou. Sem tradução o
        // operador leria o texto do trigger; o log acima guarda o erro cru.
        if (error.message?.includes('month_lock_closed')) {
            throw new Error('Mês de pagamento fechado pelo financeiro. Contate o responsável antes de alterar esta comissão.');
        }
        throw error;
    }
    return { success: true, updated: true };
};

/** Cancela (soft-delete) uma comissão — sai do pagamento (dashboard filtra canceled_at IS NULL). */
export const cancelarComissao = async ({ id }) => {
    if (!id) return { success: false };
    const { error } = await supabase.from('bd_comissoes').update({ canceled_at: new Date().toISOString() }).eq('id', id);
    if (error) { await logError(error, { fn: 'cancelarComissao', id }); throw error; }
    return { success: true };
};
